import React, { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import getApiUrl from "@/utils/api";
import { format12Hour } from "@/utils/time";
import { fmtChicagoDate, fmtChicagoTime } from "@/utils/timezone";
import { formatConfirmation, getConfirmation } from "@/utils/confirmation";
import { getPickupTimeStyle, ALL_PICKUP_BUCKETS } from "@/utils/pickupTimeColor";
import BookingNoteEditor from "./BookingNoteEditor";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { DailyCashBagCard } from "./DailyCashBagCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Calendar, Clock, Eye, CheckCircle, XCircle, CreditCard, DollarSign,
  UserCheck, Upload, FileText, Plus, Plane, PlusCircle, Send, Link2, Banknote, RotateCcw, Wallet, Users, Search, Loader2, ShieldCheck, ShieldAlert, MessageCircle, Copy, Check, Share2, AlertTriangle, Printer, ArrowLeftRight
} from "lucide-react";
import { getAirlineColors, getAirlineCode } from "@/utils/airlineUtils";
import { BookingDetailsDialog } from "@/components/BookingDetailsDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmAction } from "@/components/ConfirmAction";

const API = getApiUrl();

/** Open a clean print window with the day's pickup manifest */
function printManifest(label, dateStr, pickups) {
  const totalPax = pickups.reduce((s, b) => s + (b.num_passengers || 1), 0);
  const totalBags = pickups.reduce((s, b) => s + (b.total_suitcases || 0), 0);
  const totalCarry = pickups.reduce((s, b) => s + (b.total_carry_ons || 0), 0);
  const totalOwed = pickups.reduce((s, b) => s + (b.balance_due || 0), 0);

  const rows = pickups.map((b, i) => {
    const time = format12Hour(b.travel_time) || b.travel_time || "—";
    const bags = (b.total_suitcases || 0);
    const carry = (b.total_carry_ons || 0);
    const bal = (b.balance_due || 0);
    const driver = b.assigned_driver_name || "—";
    const pickup = b.pickup_location || b.housing_unit || "—";
    const dest = b.dropoff_location || b.housing_unit || "—";
    const pax = b.num_passengers || 1;
    const passengers = (b.passengers || []).map(p => p.name || "").filter(Boolean).join(", ") || b.full_name;
    const phone = b.whatsapp_number || "—";
    return `<tr style="border-bottom:1px solid #ddd">
      <td style="padding:6px 8px;font-weight:600">${time}</td>
      <td style="padding:6px 8px">${passengers}${pax > 1 ? ` <small>(${pax})</small>` : ""}</td>
      <td style="padding:6px 8px;font-size:11px">${phone}</td>
      <td style="padding:6px 8px">${pickup.slice(0, 35)}</td>
      <td style="padding:6px 8px">${dest.slice(0, 35)}</td>
      <td style="padding:6px 8px;text-align:center">${bags}</td>
      <td style="padding:6px 8px;text-align:center">${carry}</td>
      <td style="padding:6px 8px;text-align:right;${bal > 0 ? "color:#dc2626;font-weight:700" : "color:#16a34a"}">${bal > 0 ? "$" + bal.toFixed(0) : "Paid"}</td>
      <td style="padding:6px 8px">${b.flight_number || "—"}</td>
      <td style="padding:6px 8px">${driver}</td>
    </tr>`;
  }).join("");

  const html = `<!DOCTYPE html><html><head><title>${label} Manifest — ${dateStr}</title>
    <style>body{font-family:Arial,Helvetica,sans-serif;margin:20px;color:#111}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th{background:#1e293b;color:#fff;padding:8px;text-align:left;font-size:12px}
    h1{font-size:20px;margin:0 0 4px} .meta{color:#555;font-size:13px;margin-bottom:12px}
    @media print{body{margin:10px}}</style></head>
    <body>
      <h1>Easy Transportation — ${label} Manifest</h1>
      <div class="meta">${dateStr} &nbsp;|&nbsp; ${pickups.length} pickups &nbsp;|&nbsp; ${totalPax} passengers &nbsp;|&nbsp; ${totalBags} suitcases &nbsp;|&nbsp; ${totalCarry} carry-ons &nbsp;|&nbsp; $${totalOwed.toFixed(0)} owed</div>
      <table><thead><tr>
        <th>Time</th><th>Passengers</th><th>Phone</th><th>Pickup</th><th>Dropoff</th><th>Bags</th><th>C/O</th><th style="text-align:right">Balance</th><th>Flight</th><th>Driver</th>
      </tr></thead><tbody>${rows}</tbody>
      <tfoot><tr style="background:#f1f5f9;font-weight:700">
        <td style="padding:8px" colspan="3">TOTAL</td>
        <td style="padding:8px" colspan="2"></td>
        <td style="padding:8px;text-align:center">${totalBags}</td>
        <td style="padding:8px;text-align:center">${totalCarry}</td>
        <td style="padding:8px;text-align:right">$${totalOwed.toFixed(0)}</td>
        <td colspan="2"></td>
      </tr></tfoot></table>
      <div style="margin-top:16px;font-size:11px;color:#888">Printed ${new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })} CT</div>
    </body></html>`;

  const w = window.open("", "_blank", "width=900,height=700");
  w.document.write(html);
  w.document.close();
  w.focus();
  w.print();
}

export function BookingsTabContent({
  bookings,
  drivers,
  getAuthHeaders,
  fetchData,
  confirmBooking,
  cancelBooking,
  uncancelBooking,
  undoPayment,
  cashPayment,
  verifyPayment,
  sendConfirmation,
  getStatusBadge,
  // Filter state
  statusFilter, setStatusFilter,
  paymentFilter, setPaymentFilter,
  passengerStatusFilter, setPassengerStatusFilter,
  dateFilter, setDateFilter,
  groupFilter, setGroupFilter,
  driverFilter, setDriverFilter,
  terminalFilter, setTerminalFilter,
  sortBy, setSortBy,
  getFilteredBookings,
  hasActiveFilters,
  clearFilters,
  // Upload booking
  showUploadBooking, setShowUploadBooking,
  uploadedText, setUploadedText,
  parsedBooking, setParsedBooking,
  parseBookingText,
  submitUploadedBooking,
  // Create Custom Booking
  showCreateBooking, setShowCreateBooking,
  customBooking, setCustomBooking,
  createCustomBooking,
  airports,
  housingUnitsAdmin,
  sendMessageToPassenger,
  isItAdmin = false,
}) {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [messageDialog, setMessageDialog] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [zelleDialog, setZelleDialog] = useState(null);
  const [zelleContact, setZelleContact] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [flightLookupLoading, setFlightLookupLoading] = useState(false);
  // Merge bookings state
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState({});
  const [mergeLoading, setMergeLoading] = useState(false);
  // Quick return batch state
  const [batchReturnLoading, setBatchReturnLoading] = useState(false);
  const [showBatchReturn, setShowBatchReturn] = useState(false);
  const [batchArrivalDate, setBatchArrivalDate] = useState("");
  const [batchReturnDate, setBatchReturnDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [outstandingDebts, setOutstandingDebts] = useState([]);

  // Fetch outstanding customer debts (small summary — full CRUD in admin backend)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(getApiUrl() + "/admin/customer-debts", getAuthHeaders());
        const active = (res.data?.debts || []).filter((d) => (d.remaining_amount || 0) > 0);
        setOutstandingDebts(active);
      } catch { /* silent — non-critical */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings?.length]);

  const sendFullInfo = async (booking, e) => {
    e?.stopPropagation();
    if (!booking?.id || sendingId) return;
    setSendingId(booking.id);
    try {
      await sendConfirmation(booking.id);
      // sendConfirmation already shows a toast on success
    } finally {
      setSendingId(null);
    }
  };

  const combineWithOrdRoute = async (booking, e) => {
    e?.stopPropagation();
    if (!booking?.id) return;
    const ok = window.confirm(
      `Combine ${booking.full_name}'s ride with the O'Hare route?\n\nThis will set their trip price to $120 (O'Hare shared rate) and lock it.`
    );
    if (!ok) return;
    try {
      const existingNote = booking.notes ? `${booking.notes}\n\n` : "";
      const newNote = `${existingNote}Combined with ORD route — driver picks up on their way`;
      await axios.put(`${API}/admin/bookings/${booking.id}/edit`, {
        trip_price: 120,
        price_locked: true,
        notes: newNote,
      }, getAuthHeaders());
      toast.success(`${booking.full_name} combined with ORD route — $120 (locked)`);
      if (typeof fetchData === "function") fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to combine ride");
    }
  };

  const isMadisonOrSmallAirport = (booking) => {
    const p = (booking.pickup_location || "").toLowerCase();
    // Madison/Dane County excluded — Madison rides keep their own $80 shared rate.
    return p.includes("milwaukee") || p.includes("mke") || p.includes("midway") || p.includes("mdw");
  };

  const getConfirmationNumber = (booking) =>
    getConfirmation(booking);

  const copyConfirmation = async (booking) => {
    const conf = getConfirmationNumber(booking);
    if (!conf) return;
    const name = (booking.full_name || "").split(" ")[0] || "there";
    const dest = booking.dropoff_location || booking.housing_unit || "TBD";
    const addr = booking.housing_address ? `\n   📍 ${booking.housing_address}` : "";
    const flight = booking.flight_number ? `${booking.airline ? booking.airline + " " : ""}${booking.flight_number}` : "TBD";
    const term = booking.arrival_terminal ? ` (Terminal ${booking.arrival_terminal})` : "";
    const tripPrice = Number(booking.trip_price || 0).toFixed(0);
    const luggage = Number(booking.luggage_fee || 0);
    const tip = Number(booking.tip_amount || 0);
    const paid = Number(booking.amount_paid || 0).toFixed(0);
    const total = (booking.trip_price || 0) + (booking.luggage_fee || 0) + (booking.tip_amount || 0);
    const balance = Math.max(0, total - (booking.amount_paid || 0)).toFixed(0);
    const pax = booking.num_passengers || 1;
    const allPassengers = (booking.passengers && booking.passengers.length > 1)
      ? `\n👥 Group: ${booking.passengers.map(p => p.name).filter(Boolean).join(", ")}`
      : "";
    const dateWithDay = (() => {
      if (!booking.travel_date) return "TBD";
      const d = new Date(booking.travel_date + "T12:00:00");
      if (isNaN(d)) return booking.travel_date;
      const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
      return `${wd}, ${booking.travel_date}`;
    })();

    const text = `═══════════════════════════════
🚐 EASY TRANSPORTATION J1
   Chicago Airports ↔ Wisconsin
═══════════════════════════════

Hi ${name}!

Your Confirmation Number is:
🎟️  ${conf}

📅 Date: ${dateWithDay}
🕑 Time: ${booking.travel_time || "TBD"}
✈️  Flight: ${flight}${term}

🛬 PICKUP: ${booking.pickup_location || "TBD"}
🏠 DROP-OFF: ${dest}${addr}

👥 Passengers: ${pax}${allPassengers}

─── Payment ───
💰 Trip Price:  $${tripPrice}${luggage > 0 ? `\n🧳 Luggage:     $${luggage.toFixed(0)}` : ""}${tip > 0 ? `\n❤️  Driver Tip:  $${tip.toFixed(0)}` : ""}
💳 Paid:        $${paid}
💵 Balance:     $${balance} (due at pickup)

───────────────────────────────
📞 (815) 914-8139
🌐 easytransportationj1.com
📧 Miketransportsj1@gmail.com
───────────────────────────────`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${conf} + full booking details`);
      setCopiedId(booking.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (e) {
      toast.error("Copy failed — long-press to copy manually");
    }
  };


  // Apply passenger search on top of the existing filters
  const visibleBookings = useMemo(() => {
    const base = getFilteredBookings();
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return base;
    const qDigits = q.replace(/\D/g, "");
    return base.filter((b) => {
      const haystack = [
        b.full_name,
        b.email,
        b.whatsapp_number,
        b.confirmation_number,
        b.flight_number,
        b.pickup_location,
        b.dropoff_location,
        b.housing_unit,
        b.assigned_driver_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (haystack.includes(q)) return true;
      // Digit-only fallback for phone numbers (e.g. user types "4567" to match "(312) 555-4567")
      if (qDigits.length > 0) {
        const digits = (b.whatsapp_number || "").replace(/\D/g, "");
        if (digits.includes(qDigits)) return true;
      }
      return false;
    });
  }, [getFilteredBookings, searchQuery]);

  // Color-code arrival groups: every booking sharing a (date + airport) gets the same tint
  const GROUP_PALETTE = [
    { row: "bg-blue-200", border: "border-l-[6px] border-blue-600", chip: "bg-blue-500 text-white" },
    { row: "bg-emerald-200", border: "border-l-[6px] border-emerald-600", chip: "bg-emerald-500 text-white" },
    { row: "bg-rose-200", border: "border-l-[6px] border-rose-600", chip: "bg-rose-500 text-white" },
    { row: "bg-amber-200", border: "border-l-[6px] border-amber-600", chip: "bg-amber-500 text-white" },
    { row: "bg-violet-200", border: "border-l-[6px] border-violet-600", chip: "bg-violet-500 text-white" },
    { row: "bg-cyan-200", border: "border-l-[6px] border-cyan-600", chip: "bg-cyan-500 text-white" },
    { row: "bg-orange-200", border: "border-l-[6px] border-orange-600", chip: "bg-orange-500 text-white" },
    { row: "bg-teal-200", border: "border-l-[6px] border-teal-600", chip: "bg-teal-500 text-white" },
    { row: "bg-pink-200", border: "border-l-[6px] border-pink-600", chip: "bg-pink-500 text-white" },
    { row: "bg-lime-200", border: "border-l-[6px] border-lime-600", chip: "bg-lime-600 text-white" },
  ];
  const groupColorMap = useMemo(() => {
    const normalizeAirport = (p) => {
      const x = (p || "").toLowerCase();
      if (x.includes("o'hare") || x.includes("ohare") || x.includes("ord")) return "ohare";
      if (x.includes("midway") || x.includes("mdw")) return "midway";
      if (x.includes("milwaukee") || x.includes("mke")) return "milwaukee";
      if (x.includes("madison") || x.includes("msn")) return "madison";
      return "other";
    };
    const map = {};
    let idx = 0;
    visibleBookings.forEach((b) => {
      // Group ALL bookings (arrival + return) that share the same date and airport
      const isReturn = (b.trip_type || "arrival") === "return";
      const airportField = isReturn ? b.dropoff_location : b.pickup_location;
      const key = `${b.travel_date}__${b.trip_type || "arrival"}__${normalizeAirport(airportField)}`;
      if (!map[key]) {
        map[key] = GROUP_PALETTE[idx % GROUP_PALETTE.length];
        idx += 1;
      }
    });
    return { map, normalizeAirport };
  }, [visibleBookings]);
  const getGroupColor = (b) => {
    const isReturn = (b.trip_type || "arrival") === "return";
    const airportField = isReturn ? b.dropoff_location : b.pickup_location;
    const key = `${b.travel_date}__${b.trip_type || "arrival"}__${groupColorMap.normalizeAirport(airportField)}`;
    return groupColorMap.map[key] || null;
  };

  const verifyDeposit = async (bookingId, method) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/verify-deposit`, { method }, getAuthHeaders());
      toast.success("Deposit verified!");
      fetchData();
    } catch (e) {
      toast.error("Failed to verify deposit");
    }
  };

  const unverifyDeposit = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/unverify-deposit`, {}, getAuthHeaders());
      toast.success("Deposit unverified");
      fetchData();
    } catch (e) {
      toast.error("Failed to unverify deposit");
    }
  };

  // Handle merge bookings
  const handleMergeBookings = async () => {
    const ids = Object.entries(mergeSelected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length < 2) {
      toast.error("Select at least 2 bookings to merge");
      return;
    }
    setMergeLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/merge`, { booking_ids: ids }, getAuthHeaders());
      toast.success(res.data.message);
      setMergeMode(false);
      setMergeSelected({});
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally {
      setMergeLoading(false);
    }
  };

  // Handle batch create return trips
  const handleBatchReturn = async () => {
    if (!batchArrivalDate) {
      toast.error("Select an arrival date to find bookings from");
      return;
    }
    setBatchReturnLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/batch-return`, { travel_date: batchArrivalDate, return_date: batchReturnDate || "" }, getAuthHeaders());
      toast.success(res.data.message);
      setShowBatchReturn(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch return creation failed");
    } finally {
      setBatchReturnLoading(false);
    }
  };


  const lookupFlight = async (flightNumber, travelDate, onResult) => {
    if (!flightNumber || flightNumber.length < 3) {
      toast.error("Enter a valid flight number (e.g. UA123)");
      return;
    }
    setFlightLookupLoading(true);
    try {
      const dateParam = travelDate ? `?date=${travelDate}` : '';
      const res = await axios.get(`${API}/flight/lookup/${flightNumber.toUpperCase()}${dateParam}`);
      if (res.data.found) {
        onResult(res.data);
        const parts = [res.data.airline];
        if (res.data.arrival?.terminal) parts.push(`Terminal ${res.data.arrival.terminal}`);
        if (res.data.arrival_time_prefill) parts.push(`Arrives ${res.data.arrival_time_prefill}`);
        toast.success(parts.join(' | '));
      } else {
        toast.info("Flight not found");
      }
    } catch (e) {
      toast.error("Flight lookup failed");
    }
    setFlightLookupLoading(false);
  };

  const getMobileStatusBadge = (status, type) => {
    if (type === "booking") {
      const map = {
        pending: "bg-yellow-100 text-yellow-800",
        confirmed: "bg-green-100 text-green-800",
        cancelled: "bg-red-100 text-red-800",
      };
      return <Badge className={`text-[9px] px-1.5 py-0 ${map[status] || "bg-gray-100"}`}>{status}</Badge>;
    }
    const map = {
      pending: "bg-orange-100 text-orange-800",
      paid: "bg-green-100 text-green-800",
      pending_verification: "bg-blue-100 text-blue-800",
    };
    return <Badge className={`text-[9px] px-1.5 py-0 ${map[status] || "bg-gray-100"}`}>{status === "pending_verification" ? "verify" : status}</Badge>;
  };

  // Compute shared ride groups: same date + same airport = shared ride
  const sharedRideGroups = useMemo(() => {
    const groups = {};
    bookings.forEach(b => {
      if (b.booking_status === 'cancelled') return;
      const date = (b.travel_date || '').split('T')[0];
      const airport = b.trip_type === 'arrival' ? (b.pickup_location || '') : (b.dropoff_location || '');
      const key = `${date}|${airport}|${b.trip_type}`;
      if (!groups[key]) groups[key] = { bookings: [], totalPax: 0, totalPrice: 0, totalPaid: 0, totalBalance: 0 };
      groups[key].bookings.push(b.id);
      groups[key].totalPax += (b.num_passengers || 1);
      groups[key].totalPrice += (b.trip_price || 0);
      groups[key].totalPaid += (b.amount_paid || 0);
      groups[key].totalBalance += (b.balance_due || 0);
    });
    // Build lookup by booking id
    const lookup = {};
    Object.values(groups).forEach(g => {
      if (g.totalPax >= 2) {
        g.bookings.forEach(id => { lookup[id] = g; });
      }
    });
    return lookup;
  }, [bookings]);

  const handleTakePayment = async (bookingId) => {
    setActionLoading(true);
    try {
      const amount = parseFloat(paymentAmount);
      if (!amount || amount <= 0) { toast.error("Enter a valid amount"); setActionLoading(false); return; }
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/payment-link`, { amount }, getAuthHeaders());
      const url = res.data.checkout_url;
      navigator.clipboard.writeText(url).then(() => toast.success("Payment link copied to clipboard!")).catch(() => {});
      window.open(url, '_blank');
      setPaymentDialog(null);
      setPaymentAmount("");
    } catch (e) { toast.error("Failed to generate payment link"); }
    setActionLoading(false);
  };

  const handleRequestZelle = async (bookingId) => {
    setActionLoading(true);
    try {
      if (!zelleContact.trim()) { toast.error("Enter Zelle phone/email"); setActionLoading(false); return; }
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/request-zelle`, { zelle_contact: zelleContact }, getAuthHeaders());
      toast.success(res.data.message || "Zelle request sent!");
      setZelleDialog(null);
      setZelleContact("");
    } catch (e) { toast.error("Failed to send Zelle request"); }
    setActionLoading(false);
  };

  const handleRecalculate = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/recalculate`, {}, getAuthHeaders());
      toast.success(res.data.message || "Balances recalculated!");
      fetchData();
    } catch (e) { toast.error("Failed to recalculate"); }
    setActionLoading(false);
  };

  const [batchFlightLoading, setBatchFlightLoading] = useState(false);
  const handleBatchFlightLookup = async () => {
    setBatchFlightLoading(true);
    try {
      const res = await axios.post(`${API}/admin/batch-flight-lookup`, {}, getAuthHeaders());
      const d = res.data;
      if (d.updated > 0) {
        toast.success(`Updated ${d.updated} departure times (${d.skipped} already set, ${d.errors} not found)`);
        fetchData();
      } else if (d.skipped > 0) {
        toast.info(`All ${d.skipped} return bookings already have departure times`);
      } else {
        toast.info(d.message || "No return bookings to update");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch flight lookup failed");
    }
    setBatchFlightLoading(false);
  };

  // Today's Schedule (pinned at top): only UPCOMING pickups (not yet passed) in Chicago time, sorted by earliest time
  const todayCT = useMemo(() => {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  }, []);
  const tomorrowCT = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  }, []);
  const day3CT = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  }, []);
  const day3Label = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toLocaleDateString("en-US", { weekday: "long", timeZone: "America/Chicago" });
  }, []);
  const yesterdayCT = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  }, []);
  // Chicago current time in HH:MM (24h) — used to hide already-passed pickups
  const [nowChicago, setNowChicago] = useState(() =>
    new Intl.DateTimeFormat("en-GB", { timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date())
  );
  useEffect(() => {
    const t = setInterval(() => {
      setNowChicago(new Intl.DateTimeFormat("en-GB", { timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date()));
    }, 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const todaysPickups = useMemo(() => {
    return bookings
      .filter((b) => b.travel_date === todayCT && b.booking_status !== "cancelled")
      .sort((a, b) => (a.travel_time || "").localeCompare(b.travel_time || ""));
  }, [bookings, todayCT]);
  const tomorrowsPickups = useMemo(() => {
    return bookings
      .filter((b) => b.travel_date === tomorrowCT && b.booking_status !== "cancelled")
      .sort((a, b) => (a.travel_time || "").localeCompare(b.travel_time || ""));
  }, [bookings, tomorrowCT]);
  const day3Pickups = useMemo(() => {
    return bookings
      .filter((b) => b.travel_date === day3CT && b.booking_status !== "cancelled")
      .sort((a, b) => (a.travel_time || "").localeCompare(b.travel_time || ""));
  }, [bookings, day3CT]);

  // Last Night pickups — only visible before 5 AM Chicago time
  const chicagoHour = useMemo(() => {
    const [h] = nowChicago.split(":").map(Number);
    return h;
  }, [nowChicago]);
  const lastNightPickups = useMemo(() => {
    if (chicagoHour >= 5) return []; // Hide after 5 AM
    return bookings
      .filter((b) => b.travel_date === yesterdayCT && b.booking_status !== "cancelled")
      .sort((a, b) => (a.travel_time || "").localeCompare(b.travel_time || ""));
  }, [bookings, yesterdayCT, chicagoHour]);

  // Airport grouping helper: MSN → MKE → ORD/MDW → Other
  const getAirportKey = (b) => {
    const loc = (b.trip_type === "return" ? b.dropoff_location : b.pickup_location) || "";
    const l = loc.toLowerCase();
    if (l.includes("madison") || l.includes("msn") || l.includes("dane county")) return "MSN";
    if (l.includes("milwaukee") || l.includes("mke") || l.includes("mitchell")) return "MKE";
    if (l.includes("midway") || l.includes("mdw")) return "MDW";
    if (l.includes("o'hare") || l.includes("ohare") || l.includes("o hare") || l.includes("ord")) return "ORD";
    return "Other";
  };
  const airportOrder = { MSN: 0, MKE: 1, MDW: 2, ORD: 3, Other: 4 };
  const airportLabels = { MSN: "Madison (MSN)", MKE: "Milwaukee (MKE)", MDW: "Midway (MDW)", ORD: "O'Hare (ORD)", Other: "Other" };
  const groupByAirport = (pickups) => {
    const groups = {};
    pickups.forEach(b => {
      const key = getAirportKey(b);
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => (airportOrder[a] ?? 99) - (airportOrder[b] ?? 99))
      .map(([key, items]) => ({ key, label: airportLabels[key] || key, items: items.sort((a, b) => (a.travel_time || "").localeCompare(b.travel_time || "")) }));
  };

  /** Renders a single pickup row — responsive: stacked on mobile, full grid on desktop */
  const PickupRow = ({ b, prefix = "pickup" }) => {
    const grpColor = getGroupColor(b);
    const pickupStyle = getPickupTimeStyle(b.travel_time);
    const bal = (b.balance_due ?? 0);
    const cleanPhone = (b.whatsapp_number || "").replace(/[^0-9]/g, "");
    const passengerNames = (b.passengers || []).map(p => p.name || "").filter(Boolean);
    const checkedIn = (b.admin_notes || "").includes("Pickup time confirmed");
    return (
      <div
        onClick={() => setSelectedBooking(b)}
        className={`px-3 py-2 rounded-lg cursor-pointer transition-colors bg-white/10 backdrop-blur hover:bg-white/20 ${grpColor ? grpColor.border : ""} ${pickupStyle.row}`}
        data-testid={`${prefix}-${b.id}`}
      >
        {/* Desktop: full grid row */}
        <div className="hidden lg:grid items-center gap-x-3" style={{ gridTemplateColumns: "auto 1fr auto auto auto auto auto" }}>
          <div className="flex items-center gap-1.5">
            <span className={`font-mono font-bold text-sm px-2 py-0.5 rounded border whitespace-nowrap ${pickupStyle.badge}`} title={pickupStyle.label}>
              {pickupStyle.emoji} {format12Hour(b.travel_time) || b.travel_time || "—"}
            </span>
            {grpColor && <span className={`text-[10px] font-bold uppercase px-1 py-0.5 rounded ${grpColor.chip}`}>●</span>}
          </div>
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            <span className="font-semibold text-white text-sm truncate underline decoration-white/40 underline-offset-2 hover:decoration-white/80" onClick={(e) => { e.stopPropagation(); setSelectedBooking(b); }}>
              {b.full_name}
            </span>
            {b.num_passengers > 1 && <span className="text-[10px] bg-white/20 px-1 py-0.5 rounded whitespace-nowrap flex-shrink-0">+{b.num_passengers - 1}</span>}
            {cleanPhone && (
              <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-sky-200 hover:text-white whitespace-nowrap flex-shrink-0" title="WhatsApp">
                {b.whatsapp_number}
              </a>
            )}
            {(b.housing_unit || b.housing_address) && (
              <span className="text-[10px] bg-violet-400/30 text-violet-100 px-1.5 py-0.5 rounded-full whitespace-nowrap truncate max-w-[120px] flex-shrink-0" title={b.housing_address || b.housing_unit}>
                🏠{b.housing_unit || (b.housing_address || "").slice(0, 16)}
              </span>
            )}
          </div>
          {/* Check-in status */}
          <div className="flex items-center justify-center min-w-[70px]">
            {checkedIn ? (
              <span className="text-[10px] bg-green-400/40 text-green-50 px-2 py-0.5 rounded-full font-bold whitespace-nowrap" data-testid={`checkin-badge-${b.id}`}>Checked In</span>
            ) : (
              <span className="text-[10px] text-white/30">—</span>
            )}
          </div>
          <div className="flex items-center justify-center min-w-[60px]">
            {((b.total_suitcases || 0) > 0 || (b.total_carry_ons || 0) > 0 || (b.total_barrels || 0) > 0) ? (
              <span className="text-xs bg-white/15 text-white/90 px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                {(b.total_suitcases || 0) > 0 && `🧳${b.total_suitcases}`}{(b.total_suitcases || 0) > 0 && ((b.total_carry_ons || 0) > 0 || (b.total_barrels || 0) > 0) && " "}{(b.total_carry_ons || 0) > 0 && `🎒${b.total_carry_ons}`}{(b.total_carry_ons || 0) > 0 && (b.total_barrels || 0) > 0 && " "}{(b.total_barrels || 0) > 0 && `🛢${b.total_barrels}`}
              </span>
            ) : <span className="text-[10px] text-white/30">—</span>}
          </div>
          <div className="flex items-center justify-center min-w-[70px]">
            {bal > 0.01 ? (
              <span className="text-[11px] bg-rose-500/40 text-white px-2 py-0.5 rounded-full font-bold whitespace-nowrap">${bal.toFixed(0)} due</span>
            ) : (
              <span className="text-[11px] bg-emerald-500/30 text-emerald-50 px-2 py-0.5 rounded-full font-bold">✓ Paid</span>
            )}
          </div>
          <div className="flex items-center gap-1 min-w-[100px] justify-center">
            {b.trip_type === "return" && b.departure_time && (
              <span className="text-[10px] bg-sky-400/30 text-sky-50 px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">
                ✈{format12Hour(b.departure_time) || b.departure_time}
              </span>
            )}
            <span className="text-[10px] text-white/70 truncate max-w-[100px]">
              {b.flight_number || ""}
            </span>
          </div>
          <div className="flex items-center justify-end min-w-[80px]">
            {b.assigned_driver_name ? (
              <span className="text-[11px] bg-emerald-400/30 text-emerald-50 px-2 py-0.5 rounded-full font-bold whitespace-nowrap truncate max-w-[110px]">🚗 {b.assigned_driver_name}</span>
            ) : (
              <span className="text-[11px] bg-rose-400/30 text-rose-50 px-2 py-0.5 rounded-full font-bold whitespace-nowrap">No driver</span>
            )}
          </div>
        </div>

        {/* Mobile/Tablet: stacked layout with prominent name */}
        <div className="lg:hidden space-y-1.5">
          {/* Row 1: Time + Name (large, never truncated) */}
          <div className="flex items-start gap-2">
            <span className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded border whitespace-nowrap flex-shrink-0 ${pickupStyle.badge}`} title={pickupStyle.label}>
              {pickupStyle.emoji} {format12Hour(b.travel_time) || b.travel_time || "—"}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-white text-sm leading-tight break-words" data-testid={`${prefix}-name-mobile-${b.id}`} onClick={(e) => { e.stopPropagation(); setSelectedBooking(b); }}>
                {b.full_name}
                {b.num_passengers > 1 && <span className="ml-1 text-[10px] bg-white/20 px-1 py-0.5 rounded align-middle">+{b.num_passengers - 1}</span>}
                {checkedIn && <span className="ml-1 text-[10px] bg-green-400/40 text-green-50 px-1.5 py-0.5 rounded-full font-bold align-middle" data-testid={`checkin-badge-mobile-${b.id}`}>Checked In</span>}
              </p>
              {/* Show all passenger names for group bookings */}
              {passengerNames.length > 1 && (
                <p className="text-[11px] text-white/80 leading-snug mt-0.5 break-words">
                  {passengerNames.join(", ")}
                </p>
              )}
            </div>
            {grpColor && <span className={`text-[10px] font-bold uppercase px-1 py-0.5 rounded flex-shrink-0 ${grpColor.chip}`}>●</span>}
          </div>
          {/* Row 2: Details chips */}
          <div className="flex flex-wrap items-center gap-1.5 pl-0">
            {cleanPhone && (
              <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-sky-200 hover:text-white whitespace-nowrap" title="WhatsApp">
                {b.whatsapp_number}
              </a>
            )}
            {(b.housing_unit || b.housing_address) && (
              <span className="text-[10px] bg-violet-400/30 text-violet-100 px-1.5 py-0.5 rounded-full whitespace-nowrap" title={b.housing_address || b.housing_unit}>
                🏠{b.housing_unit || (b.housing_address || "").slice(0, 20)}
              </span>
            )}
            {((b.total_suitcases || 0) > 0 || (b.total_carry_ons || 0) > 0 || (b.total_barrels || 0) > 0) && (
              <span className="text-[10px] bg-white/15 text-white/90 px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">
                {(b.total_suitcases || 0) > 0 && `🧳${b.total_suitcases}`}{(b.total_suitcases || 0) > 0 && ((b.total_carry_ons || 0) > 0 || (b.total_barrels || 0) > 0) && " "}{(b.total_carry_ons || 0) > 0 && `🎒${b.total_carry_ons}`}{(b.total_carry_ons || 0) > 0 && (b.total_barrels || 0) > 0 && " "}{(b.total_barrels || 0) > 0 && `🛢${b.total_barrels}`}
              </span>
            )}
            {bal > 0.01 ? (
              <span className="text-[10px] bg-rose-500/40 text-white px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">${bal.toFixed(0)} due</span>
            ) : (
              <span className="text-[10px] bg-emerald-500/30 text-emerald-50 px-1.5 py-0.5 rounded-full font-bold">✓ Paid</span>
            )}
            {b.flight_number && (
              <span className="text-[10px] text-white/70 whitespace-nowrap">✈ {b.flight_number}</span>
            )}
            {b.assigned_driver_name ? (
              <span className="text-[10px] bg-emerald-400/30 text-emerald-50 px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">🚗 {b.assigned_driver_name}</span>
            ) : (
              <span className="text-[10px] bg-rose-400/30 text-rose-50 px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap">No driver</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  /** Renders grouped pickup list by airport */
  const AirportGroupedList = ({ pickups, prefix }) => {
    const groups = groupByAirport(pickups);
    if (groups.length <= 1) {
      // Single airport or ungrouped — render flat
      return pickups.map(b => <PickupRow key={b.id} b={b} prefix={prefix} />);
    }
    return groups.map(g => (
      <div key={g.key}>
        <div className="flex items-center gap-2 mt-2 mb-1 first:mt-0">
          <Plane className="w-3 h-3 text-white/60" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-white/70">{g.label} ({g.items.length})</span>
          <div className="flex-1 border-t border-white/20"></div>
        </div>
        {g.items.map(b => <PickupRow key={b.id} b={b} prefix={prefix} />)}
      </div>
    ));
  };

  /** Summary stats for a set of pickups */
  const pickupStats = (pickups) => ({
    count: pickups.length,
    pax: pickups.reduce((s, b) => s + (b.num_passengers || 1), 0),
    bags: pickups.reduce((s, b) => s + (b.total_suitcases || 0), 0),
    carry: pickups.reduce((s, b) => s + (b.total_carry_ons || 0), 0),
    barrels: pickups.reduce((s, b) => s + (b.total_barrels || 0), 0),
    owed: pickups.reduce((s, b) => s + (b.balance_due || 0), 0),
  });

  return (
    <>
    <DailyCashBagCard />
    {outstandingDebts.length > 0 && (
      <Card className="mb-4 border-2 border-red-400 shadow-lg rounded-2xl bg-gradient-to-r from-red-50 via-rose-50 to-red-50" data-testid="customer-debts-card">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-red-700" />
            <CardTitle className="text-red-900 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Outstanding Customer Balances
            </CardTitle>
            <span className="ml-auto text-xs font-bold bg-red-600 text-white px-2 py-0.5 rounded-full">
              {outstandingDebts.length}
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1.5">
            {outstandingDebts.map((d) => (
              <div key={d.phone} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/70 border border-red-200" data-testid={`debt-row-${d.phone}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-mono font-bold text-red-900 text-sm">{d.phone}</p>
                  {d.notes && <p className="text-[10px] text-red-800 truncate">{d.notes}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-red-700">Auto-charge next booking:</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${d.auto_apply_active ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-gray-100 text-gray-600 border border-gray-300'}`}>
                    {d.auto_apply_active ? `ON · +$${(d.surcharge_per_booking || 50).toFixed(0)}` : 'OFF'}
                  </span>
                  <span className="text-base font-extrabold text-red-800" data-testid={`debt-amount-${d.phone}`}>
                    OWES ${(d.remaining_amount || 0).toFixed(0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}
    {/* Last Night's pickups — visible before 5 AM only */}
    {lastNightPickups.length > 0 && (
      <Card className="mb-4 border-0 shadow-lg rounded-2xl bg-gradient-to-r from-gray-700 via-slate-700 to-gray-800 text-white opacity-80" data-testid="last-night-schedule">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <CardTitle className="text-white text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Last Night · {yesterdayCT}
              </CardTitle>
              <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">Hides at 5 AM</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-xs font-bold bg-white/20 backdrop-blur px-3 py-1 rounded-full">
                {lastNightPickups.length} pickup{lastNightPickups.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
            <AirportGroupedList pickups={lastNightPickups} prefix="lastnight-pickup" />
          </div>
        </CardContent>
      </Card>
    )}

    {todaysPickups.length > 0 && (
      <Card className="mb-4 border-0 shadow-lg rounded-2xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 text-white" data-testid="todays-schedule">
      <CardHeader className="pb-3">        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <CardTitle className="text-white text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Today's Schedule · {todayCT}
            </CardTitle>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <span className="text-xs font-bold bg-white/20 backdrop-blur px-3 py-1 rounded-full">
              {todaysPickups.length} pickup{todaysPickups.length !== 1 ? "s" : ""} · {todaysPickups.reduce((s, b) => s + (b.num_passengers || 1), 0)} pax
            </span>
            <span className="text-xs font-bold bg-white/20 backdrop-blur px-2 py-1 rounded-full" data-testid="today-luggage-count">
              🧳 {todaysPickups.reduce((s, b) => s + (b.total_suitcases || 0), 0)} · 🎒 {todaysPickups.reduce((s, b) => s + (b.total_carry_ons || 0), 0)}
            </span>
            <span className="text-xs font-bold bg-rose-500/30 backdrop-blur px-2 py-1 rounded-full" data-testid="today-total-balance">
              ${todaysPickups.reduce((s, b) => s + (b.balance_due || 0), 0).toFixed(0)} owed
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); printManifest("Today's Schedule", todayCT, todaysPickups); }}
              className="text-[10px] font-bold bg-white/20 hover:bg-white/40 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 transition-colors"
              data-testid="print-today-manifest"
              title="Print today's manifest"
            >
              <Printer className="w-3 h-3" /> Print
            </button>
          </div>
        </div>
        {/* Pickup-time color legend */}
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="pickup-time-legend">
          {ALL_PICKUP_BUCKETS.map((bucket) => (
            <span key={bucket.key} className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${bucket.badge}`}>
              {bucket.emoji} {bucket.label}
            </span>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto">
          <AirportGroupedList pickups={todaysPickups} prefix="today-pickup" />
        </div>
        {todaysPickups.length > 1 && (() => {
          const s = pickupStats(todaysPickups);
          return (
            <div className="flex items-center gap-2 py-2 px-2 mt-2" data-testid="today-bottom-summary">
              <div className="flex-1 border-t border-white/30"></div>
              <span className="text-[10px] font-bold bg-white/25 backdrop-blur text-white px-3 py-0.5 rounded-full whitespace-nowrap">
                Total: {s.pax} pax · 🧳{s.bags} · 🎒{s.carry}{s.barrels > 0 ? ` · 🛢${s.barrels}` : ""} · ${s.owed.toFixed(0)} owed
              </span>
              <div className="flex-1 border-t border-white/30"></div>
            </div>
          );
        })()}
      </CardContent>
    </Card>
    )}

    {/* Tomorrow's Pickups — all bookings for the next calendar day, sorted by time, color-coded by group */}
    {tomorrowsPickups.length > 0 && (
      <Card className="mb-4 border-0 shadow-lg rounded-2xl bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white" data-testid="tomorrows-schedule">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <CardTitle className="text-white text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Tomorrow's Pickups · {tomorrowCT}
              </CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-xs font-bold bg-white/20 backdrop-blur px-3 py-1 rounded-full" data-testid="tomorrows-pickups-count">
                {tomorrowsPickups.length} pickup{tomorrowsPickups.length !== 1 ? "s" : ""} · {tomorrowsPickups.reduce((s, b) => s + (b.num_passengers || 1), 0)} pax
              </span>
              <span className="text-xs font-bold bg-white/20 backdrop-blur px-2 py-1 rounded-full" data-testid="tomorrow-luggage-count">
                🧳 {tomorrowsPickups.reduce((s, b) => s + (b.total_suitcases || 0), 0)} · 🎒 {tomorrowsPickups.reduce((s, b) => s + (b.total_carry_ons || 0), 0)}
              </span>
              <span className="text-xs font-bold bg-rose-500/30 backdrop-blur px-2 py-1 rounded-full" data-testid="tomorrow-total-balance">
                ${tomorrowsPickups.reduce((s, b) => s + (b.balance_due || 0), 0).toFixed(0)} owed
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); printManifest("Tomorrow's Pickups", tomorrowCT, tomorrowsPickups); }}
                className="text-[10px] font-bold bg-white/20 hover:bg-white/40 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 transition-colors"
                data-testid="print-tomorrow-manifest"
                title="Print tomorrow's manifest"
              >
                <Printer className="w-3 h-3" /> Print
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1.5 max-h-[340px] overflow-y-auto">
            <AirportGroupedList pickups={tomorrowsPickups} prefix="tomorrow-pickup" />
          </div>
          {tomorrowsPickups.length > 1 && (() => {
            const s = pickupStats(tomorrowsPickups);
            return (
              <div className="flex items-center gap-2 py-2 px-2 mt-2" data-testid="tomorrow-bottom-summary">
                <div className="flex-1 border-t border-white/30"></div>
                <span className="text-[10px] font-bold bg-white/25 backdrop-blur text-white px-3 py-0.5 rounded-full whitespace-nowrap">
                  Total: {s.pax} pax · 🧳{s.bags} · 🎒{s.carry}{s.barrels > 0 ? ` · 🛢${s.barrels}` : ""} · ${s.owed.toFixed(0)} owed
                </span>
                <div className="flex-1 border-t border-white/30"></div>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    )}

    {/* Day 3 Pickups — two days from now */}
    {day3Pickups.length > 0 && (
      <Card className="mb-4 border-0 shadow-lg rounded-2xl bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white" data-testid="day3-schedule">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <CardTitle className="text-white text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {day3Label}'s Pickups · {day3CT}
              </CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-xs font-bold bg-white/20 backdrop-blur px-3 py-1 rounded-full" data-testid="day3-pickups-count">
                {day3Pickups.length} pickup{day3Pickups.length !== 1 ? "s" : ""} · {day3Pickups.reduce((s, b) => s + (b.num_passengers || 1), 0)} pax
              </span>
              <span className="text-xs font-bold bg-white/20 backdrop-blur px-2 py-1 rounded-full" data-testid="day3-luggage-count">
                🧳 {day3Pickups.reduce((s, b) => s + (b.total_suitcases || 0), 0)} · 🎒 {day3Pickups.reduce((s, b) => s + (b.total_carry_ons || 0), 0)}
              </span>
              <span className="text-xs font-bold bg-rose-500/30 backdrop-blur px-2 py-1 rounded-full" data-testid="day3-total-balance">
                ${day3Pickups.reduce((s, b) => s + (b.balance_due || 0), 0).toFixed(0)} owed
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); printManifest(`${day3Label}'s Pickups`, day3CT, day3Pickups); }}
                className="text-[10px] font-bold bg-white/20 hover:bg-white/40 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 transition-colors"
                data-testid="print-day3-manifest"
                title="Print day 3 manifest"
              >
                <Printer className="w-3 h-3" /> Print
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1.5 max-h-[340px] overflow-y-auto">
            <AirportGroupedList pickups={day3Pickups} prefix="day3-pickup" />
          </div>
          {day3Pickups.length > 1 && (() => {
            const s = pickupStats(day3Pickups);
            return (
              <div className="flex items-center gap-2 py-2 px-2 mt-2" data-testid="day3-bottom-summary">
                <div className="flex-1 border-t border-white/30"></div>
                <span className="text-[10px] font-bold bg-white/25 backdrop-blur text-white px-3 py-0.5 rounded-full whitespace-nowrap">
                  Total: {s.pax} pax · 🧳{s.bags} · 🎒{s.carry}{s.barrels > 0 ? ` · 🛢${s.barrels}` : ""} · ${s.owed.toFixed(0)} owed
                </span>
                <div className="flex-1 border-t border-white/30"></div>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    )}

    {/* Global Booking Details Dialog — opens when clicking names on pickup cards */}
    <Dialog open={!!selectedBooking} onOpenChange={(open) => { if (!open) setSelectedBooking(null); }}>
      <BookingDetailsDialog selectedBooking={selectedBooking} drivers={drivers} getAuthHeaders={getAuthHeaders} fetchData={fetchData} onClose={() => setSelectedBooking(null)}
        onCreateReturnTrip={(b) => {
          setSelectedBooking(null);
          setCustomBooking({
            trip_type: "return",
            ride_type: b.ride_type || "shared",
            full_name: b.full_name || "",
            whatsapp_number: b.whatsapp_number || "",
            email: b.email || "",
            travel_date: "",
            travel_time: "",
            departure_time: "",
            flight_number: "",
            airline: "",
            pickup_location: b.dropoff_location || b.housing_unit || "",
            dropoff_location: b.pickup_location || "",
            housing_unit: b.dropoff_location || b.housing_unit || "",
            num_passengers: b.num_passengers || 1,
            total_suitcases: b.total_suitcases || 0,
            total_carry_ons: b.total_carry_ons || 0,
            total_barrels: b.total_barrels || 0,
            uses_shipping_company: b.uses_shipping_company || false,
            passengers: (b.passengers || []).map(p => ({
              name: p.name || "",
              whatsapp: p.whatsapp || "",
              housing_unit: p.housing_unit || "",
              pickup_location: p.pickup_location || "",
              dropoff_location: p.dropoff_location || "",
            })),
          });
          setShowCreateBooking(true);
        }}
      />
    </Dialog>

    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>All Bookings</CardTitle>
          
          <div className="flex flex-wrap gap-2 items-center">
            {/* Recalculate Balances */}
            <Button variant="outline" size="sm" onClick={handleRecalculate} disabled={actionLoading} className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="recalculate-btn">
              <DollarSign className="w-4 h-4 mr-1" />
              {actionLoading ? "Recalculating..." : "Recalculate Balances"}
            </Button>

            {/* Batch Flight Lookup */}
            <Button variant="outline" size="sm" onClick={handleBatchFlightLookup} disabled={batchFlightLoading} className="bg-sky-50 border-sky-300 text-sky-700 hover:bg-sky-100" data-testid="batch-flight-lookup-btn">
              <Plane className="w-4 h-4 mr-1" />
              {batchFlightLoading ? "Looking up flights..." : "Batch Flight Lookup"}
            </Button>

            {/* Quick Return Batch */}
            <Button variant="outline" size="sm" onClick={() => setShowBatchReturn(!showBatchReturn)} className={showBatchReturn ? "bg-blue-100 border-blue-400 text-blue-700" : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"} data-testid="batch-return-btn">
              <ArrowLeftRight className="w-4 h-4 mr-1" />
              Batch Return Trips
            </Button>

            {/* Merge Bookings Toggle */}
            <Button variant="outline" size="sm" onClick={() => { setMergeMode(!mergeMode); setMergeSelected({}); }} className={mergeMode ? "bg-purple-100 border-purple-400 text-purple-700" : "bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"} data-testid="merge-mode-btn">
              <Users className="w-4 h-4 mr-1" />
              {mergeMode ? "Cancel Merge" : "Merge Bookings"}
            </Button>
            {mergeMode && (
              <Button size="sm" onClick={handleMergeBookings} disabled={mergeLoading || Object.values(mergeSelected).filter(Boolean).length < 2} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="merge-confirm-btn">
                {mergeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Merge ({Object.values(mergeSelected).filter(Boolean).length}) Selected
              </Button>
            )}

            {/* Create Custom Booking */}
            <Dialog open={showCreateBooking} onOpenChange={setShowCreateBooking}>
              <DialogTrigger asChild>
                <Button variant="default" size="sm" className="bg-green-600 hover:bg-green-700" data-testid="create-custom-booking-btn">
                  <PlusCircle className="w-4 h-4 mr-1" />
                  Create Booking
                </Button>
              </DialogTrigger>

          {/* Batch Return Date Picker Panel */}
          {showBatchReturn && (
            <div className="col-span-full border-2 border-blue-300 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 -mt-2" data-testid="batch-return-panel">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-bold text-blue-900 flex items-center gap-2 text-sm">
                    <ArrowLeftRight className="w-4 h-4" /> Batch Create Return Trips
                  </h4>
                  <p className="text-xs text-blue-700 mt-1">Creates return drafts for all arrival bookings on the selected date. You can fill in flight numbers afterwards.</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setShowBatchReturn(false)} className="text-blue-600 h-7 w-7 p-0">
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-xs font-semibold text-blue-800">Arrival Date (find bookings from) *</Label>
                  <Input type="date" value={batchArrivalDate} onChange={(e) => setBatchArrivalDate(e.target.value)} className="h-9 w-44 mt-1 border-blue-300" data-testid="batch-arrival-date" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-blue-800">Return Date (assign to all)</Label>
                  <Input type="date" value={batchReturnDate} onChange={(e) => setBatchReturnDate(e.target.value)} className="h-9 w-44 mt-1 border-blue-300" data-testid="batch-return-date" />
                  <p className="text-[10px] text-blue-600 mt-0.5">Leave blank to set individually later</p>
                </div>
                <Button size="sm" onClick={handleBatchReturn} disabled={batchReturnLoading || !batchArrivalDate} className="bg-blue-600 hover:bg-blue-700 text-white h-9" data-testid="batch-return-confirm">
                  {batchReturnLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowLeftRight className="w-4 h-4 mr-1" />}
                  {batchReturnLoading ? "Creating..." : "Create Return Drafts"}
                </Button>
              </div>
            </div>
          )}
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <PlusCircle className="w-5 h-5 text-green-600" />
                    Create Custom Booking
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Trip Type *</Label>
                      <Select value={customBooking?.trip_type || "arrival"} onValueChange={(v) => setCustomBooking(p => ({...p, trip_type: v}))}>
                        <SelectTrigger className="h-9" data-testid="custom-trip-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="arrival">Arrival (Airport → Housing)</SelectItem>
                          <SelectItem value="return">Return (Housing → Airport)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Ride Type</Label>
                      <Select value={customBooking?.ride_type || "shared"} onValueChange={(v) => setCustomBooking(p => ({...p, ride_type: v}))}>
                        <SelectTrigger className="h-9" data-testid="custom-ride-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shared">Shared Ride</SelectItem>
                          <SelectItem value="private">Private Ride</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={customBooking?.booking_status || "confirmed"} onValueChange={(v) => setCustomBooking(p => ({...p, booking_status: v}))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {customBooking?.trip_type === "return" && (
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-orange-200 bg-orange-50 w-full h-9">
                          <input type="checkbox" checked={!!customBooking?.uses_shipping_company} onChange={(e) => setCustomBooking(p => ({...p, uses_shipping_company: e.target.checked}))} className="w-4 h-4 accent-orange-600" data-testid="custom-uses-shipping" />
                          <span className="text-xs font-medium text-orange-800">Ship with T&M Courier</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Full Name *</Label>
                      <Input value={customBooking?.full_name || ""} onChange={(e) => setCustomBooking(p => ({...p, full_name: e.target.value}))} className="h-9" data-testid="custom-full-name" />
                    </div>
                    <div>
                      <Label className="text-xs">WhatsApp Number *</Label>
                      <Input value={customBooking?.whatsapp_number || ""} onChange={(e) => setCustomBooking(p => ({...p, whatsapp_number: e.target.value}))} className="h-9" placeholder="+1..." data-testid="custom-whatsapp" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Travel Date *</Label>
                      <Input type="date" value={customBooking?.travel_date || ""} onChange={(e) => setCustomBooking(p => ({...p, travel_date: e.target.value}))} className="h-9" data-testid="custom-travel-date" />
                    </div>
                    <div>
                      <Label className="text-xs">{customBooking?.trip_type === "arrival" ? "Arrival Time" : "Pickup Time"}</Label>
                      <Input type="time" value={customBooking?.travel_time || ""} onChange={(e) => setCustomBooking(p => ({...p, travel_time: e.target.value}))} className="h-9" data-testid="custom-travel-time" />
                    </div>
                  </div>
                  {customBooking?.trip_type === "return" && (
                    <div>
                      <Label className="text-xs">Departure/Flight Time</Label>
                      <Input type="time" value={customBooking?.departure_time || ""} onChange={(e) => setCustomBooking(p => ({...p, departure_time: e.target.value}))} className="h-9" data-testid="custom-departure-time" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Flight Number</Label>
                      <div className="flex gap-1">
                        <Input value={customBooking?.flight_number || ""} onChange={(e) => setCustomBooking(p => ({...p, flight_number: e.target.value.toUpperCase()}))} className="h-9 uppercase flex-1" placeholder="e.g. UA123" data-testid="custom-flight-number" />
                        <Button
                          type="button" variant="outline" size="sm"
                          className="h-9 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                          disabled={flightLookupLoading || !customBooking?.flight_number}
                          onClick={() => lookupFlight(customBooking.flight_number, customBooking.travel_date, (data) => {
                            setCustomBooking(p => ({
                              ...p,
                              airline: data.airline || p.airline,
                              travel_time: data.arrival_time_prefill || p.travel_time,
                            }));
                          })}
                          data-testid="custom-flight-lookup-btn"
                        >
                          {flightLookupLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Airline</Label>
                      <Input value={customBooking?.airline || ""} onChange={(e) => setCustomBooking(p => ({...p, airline: e.target.value}))} className="h-9" data-testid="custom-airline" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{customBooking?.trip_type === "return" ? "Pickup (Housing/Address) *" : "Airport *"}</Label>
                      {customBooking?.trip_type === "return" ? (
                        <AddressAutocomplete
                          value={customBooking?.pickup_location || ""}
                          onChange={(v) => setCustomBooking(p => ({...p, pickup_location: v, housing_unit: v}))}
                          placeholder="Housing name or address"
                          dataTestId="custom-pickup-location"
                        />
                      ) : (
                        <Select value={customBooking?.pickup_location || ""} onValueChange={(v) => setCustomBooking(p => ({...p, pickup_location: v}))}>
                          <SelectTrigger className="h-9" data-testid="custom-airport"><SelectValue placeholder="Select airport" /></SelectTrigger>
                          <SelectContent>
                            {(airports || []).map(a => (
                              <SelectItem key={a.id} value={a.name}>{a.name} ({a.code})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">{customBooking?.trip_type === "return" ? "Airport (Dropoff) *" : "Housing/Destination *"}</Label>
                      {customBooking?.trip_type === "return" ? (
                        <Select value={customBooking?.dropoff_location || ""} onValueChange={(v) => setCustomBooking(p => ({...p, dropoff_location: v}))}>
                          <SelectTrigger className="h-9" data-testid="custom-dropoff-airport"><SelectValue placeholder="Select airport" /></SelectTrigger>
                          <SelectContent>
                            {(airports || []).map(a => (
                              <SelectItem key={a.id} value={a.name}>{a.name} ({a.code})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <AddressAutocomplete
                          value={customBooking?.housing_unit || ""}
                          onChange={(v) => setCustomBooking(p => ({...p, housing_unit: v, dropoff_location: v}))}
                          placeholder="Housing name or full address"
                          dataTestId="custom-housing"
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs"># Passengers</Label>
                      <Input type="number" min={1} value={customBooking?.num_passengers || 1} onChange={(e) => setCustomBooking(p => ({...p, num_passengers: parseInt(e.target.value) || 1}))} className="h-9" />
                    </div>
                    <div>
                      <Label className="text-xs">Suitcases</Label>
                      <Input type="number" min={0} value={customBooking?.total_suitcases || 0} onChange={(e) => setCustomBooking(p => ({...p, total_suitcases: parseInt(e.target.value) || 0}))} className="h-9" data-testid="custom-total-suitcases" />
                    </div>
                    <div>
                      <Label className="text-xs">Carry-ons</Label>
                      <Input type="number" min={0} value={customBooking?.total_carry_ons || 0} onChange={(e) => setCustomBooking(p => ({...p, total_carry_ons: parseInt(e.target.value) || 0}))} className="h-9" data-testid="custom-total-carry-ons" />
                    </div>
                    <div>
                      <Label className="text-xs">Barrels</Label>
                      <Input type="number" min={0} value={customBooking?.total_barrels || 0} onChange={(e) => setCustomBooking(p => ({...p, total_barrels: parseInt(e.target.value) || 0}))} className="h-9" data-testid="custom-total-barrels" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Payment Method</Label>
                      <Select value={customBooking?.payment_method || "manual"} onValueChange={(v) => setCustomBooking(p => ({...p, payment_method: v}))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="stripe">Stripe</SelectItem>
                          <SelectItem value="cashapp">CashApp</SelectItem>
                          <SelectItem value="zelle">Zelle</SelectItem>
                          <SelectItem value="paypal">PayPal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Payment Status</Label>
                      <Select value={customBooking?.payment_status || "pending"} onValueChange={(v) => setCustomBooking(p => ({...p, payment_status: v}))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="pending_verification">Verify</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input value={customBooking?.email || ""} onChange={(e) => setCustomBooking(p => ({...p, email: e.target.value}))} className="h-9" placeholder="passenger@email.com" />
                  </div>
                  <div>
                    <Label className="text-xs">Admin Notes</Label>
                    <Textarea value={customBooking?.admin_notes || ""} onChange={(e) => setCustomBooking(p => ({...p, admin_notes: e.target.value}))} className="h-16" placeholder="Internal notes..." />
                  </div>
                  <Button onClick={createCustomBooking} className="w-full bg-green-600 hover:bg-green-700" data-testid="submit-custom-booking-btn">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Create Booking
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showUploadBooking} onOpenChange={setShowUploadBooking}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100">
                  <Upload className="w-4 h-4 mr-1" />
                  Upload Booking
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Upload Booking Information
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Paste booking details (from email, WhatsApp, etc.)</Label>
                    <Textarea
                      value={uploadedText}
                      onChange={(e) => setUploadedText(e.target.value)}
                      placeholder={"Paste booking info here...\nExample:\nName: John Doe\nPhone: 347-111-1111\nDate: 03/15/2026\nTime: 2:00 PM\nFlight: UA123\nHousing: Rainbow Housing"}
                      className="h-40 mt-2"
                    />
                    <Button 
                      onClick={() => parseBookingText(uploadedText)} 
                      variant="outline" 
                      className="mt-2"
                      disabled={!uploadedText.trim()}
                    >
                      Parse Text
                    </Button>
                  </div>
                  
                  {parsedBooking && (
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold text-sm">Parsed Information (edit if needed):</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Full Name *</Label>
                          <Input value={parsedBooking.full_name} onChange={(e) => setParsedBooking({...parsedBooking, full_name: e.target.value})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">WhatsApp *</Label>
                          <Input value={parsedBooking.whatsapp_number} onChange={(e) => setParsedBooking({...parsedBooking, whatsapp_number: e.target.value})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">Trip Type *</Label>
                          <Select value={parsedBooking.trip_type || "arrival"} onValueChange={(v) => setParsedBooking({...parsedBooking, trip_type: v})}>
                            <SelectTrigger className="h-9" data-testid="upload-trip-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="arrival">Arrival (Airport → Housing)</SelectItem>
                              <SelectItem value="return">Return (Housing → Airport)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Ride Type</Label>
                          <Select value={parsedBooking.ride_type || "shared"} onValueChange={(v) => setParsedBooking({...parsedBooking, ride_type: v})}>
                            <SelectTrigger className="h-9" data-testid="upload-ride-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="shared">Shared Ride</SelectItem>
                              <SelectItem value="private">Private Ride</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Travel Date *</Label>
                          <Input type="date" value={parsedBooking.travel_date} onChange={(e) => setParsedBooking({...parsedBooking, travel_date: e.target.value})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">{parsedBooking.trip_type === "return" ? "Departure Time" : "Arrival Time"}</Label>
                          <Input type="time" value={parsedBooking.travel_time} onChange={(e) => setParsedBooking({...parsedBooking, travel_time: e.target.value})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">Flight Number</Label>
                          <div className="flex gap-1">
                            <Input value={parsedBooking.flight_number} onChange={(e) => setParsedBooking({...parsedBooking, flight_number: e.target.value})} className="h-9 uppercase flex-1" />
                            <Button
                              type="button" variant="outline" size="sm"
                              className="h-9 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                              disabled={flightLookupLoading || !parsedBooking.flight_number}
                              onClick={() => lookupFlight(parsedBooking.flight_number, parsedBooking.travel_date, (data) => {
                                setParsedBooking(prev => ({
                                  ...prev,
                                  airline: data.airline || prev.airline,
                                  travel_time: (prev.trip_type === "return" ? data.departure_time_prefill : data.arrival_time_prefill) || prev.travel_time,
                                }));
                              })}
                              data-testid="parsed-flight-lookup-btn"
                            >
                              {flightLookupLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Airline</Label>
                          <Input value={parsedBooking.airline} onChange={(e) => setParsedBooking({...parsedBooking, airline: e.target.value})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">{parsedBooking.trip_type === "return" ? "Pickup (Housing) *" : "Airport *"}</Label>
                          {parsedBooking.trip_type === "return" ? (
                            <AddressAutocomplete value={parsedBooking.pickup_location || ""} onChange={(v) => setParsedBooking({...parsedBooking, pickup_location: v, housing_unit: v})} placeholder="Housing / pickup address" dataTestId="upload-pickup" />
                          ) : (
                            <Select value={parsedBooking.pickup_location || ""} onValueChange={(v) => setParsedBooking({...parsedBooking, pickup_location: v})}>
                              <SelectTrigger className="h-9" data-testid="upload-airport"><SelectValue placeholder="Select airport" /></SelectTrigger>
                              <SelectContent>
                                {(airports || []).map(a => (
                                  <SelectItem key={a.id} value={a.name}>{a.name} ({a.code})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">{parsedBooking.trip_type === "return" ? "Airport (Dropoff) *" : "Housing/Destination *"}</Label>
                          {parsedBooking.trip_type === "return" ? (
                            <Select value={parsedBooking.dropoff_location || parsedBooking.housing_unit || ""} onValueChange={(v) => setParsedBooking({...parsedBooking, dropoff_location: v})}>
                              <SelectTrigger className="h-9" data-testid="upload-dropoff-airport"><SelectValue placeholder="Select airport" /></SelectTrigger>
                              <SelectContent>
                                {(airports || []).map(a => (
                                  <SelectItem key={a.id} value={a.name}>{a.name} ({a.code})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <AddressAutocomplete value={parsedBooking.housing_unit || ""} onChange={(v) => setParsedBooking({...parsedBooking, housing_unit: v, dropoff_location: v})} placeholder="Housing / destination" dataTestId="upload-housing" />
                          )}
                        </div>
                        <div>
                          <Label className="text-xs"># Passengers</Label>
                          <Input type="number" value={parsedBooking.num_passengers} onChange={(e) => setParsedBooking({...parsedBooking, num_passengers: parseInt(e.target.value) || 1})} className="h-9" min={1} />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input value={parsedBooking.email || ""} onChange={(e) => setParsedBooking({...parsedBooking, email: e.target.value})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">Suitcases</Label>
                          <Input type="number" min={0} value={parsedBooking.total_suitcases || 0} onChange={(e) => setParsedBooking({...parsedBooking, total_suitcases: parseInt(e.target.value) || 0})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">Carry-ons</Label>
                          <Input type="number" min={0} value={parsedBooking.total_carry_ons || 0} onChange={(e) => setParsedBooking({...parsedBooking, total_carry_ons: parseInt(e.target.value) || 0})} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-xs">Barrels</Label>
                          <Input type="number" min={0} value={parsedBooking.total_barrels || 0} onChange={(e) => setParsedBooking({...parsedBooking, total_barrels: parseInt(e.target.value) || 0})} className="h-9" data-testid="upload-barrels" />
                        </div>
                        {parsedBooking.trip_type === "return" && (
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-orange-200 bg-orange-50 w-full h-9">
                              <input type="checkbox" checked={!!parsedBooking.uses_shipping_company} onChange={(e) => setParsedBooking({...parsedBooking, uses_shipping_company: e.target.checked})} className="w-4 h-4 accent-orange-600" data-testid="upload-uses-shipping" />
                              <span className="text-xs font-medium text-orange-800">Ship with T&M Courier</span>
                            </label>
                          </div>
                        )}
                      </div>
                      <Button onClick={submitUploadedBooking} className="w-full mt-4 bg-green-600 hover:bg-green-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Booking
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700">
                <XCircle className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
            
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-sm border rounded-lg bg-white"
            >
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="earliest_arrival">⏰ Earliest Arrival (per day)</option>
              <option value="time">By Time</option>
              <option value="name">By Name</option>
              <option value="amount">By Amount</option>
            </select>
          </div>
        </div>

        {/* Passenger Search Bar */}
        <div className="mt-3 md:mt-4">
          <div className="relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search passengers by name, email, phone, confirmation #, flight, housing, or driver..."
              className="pl-9 pr-9 h-10 rounded-xl border-gray-300 focus-visible:ring-amber-400"
              data-testid="passenger-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                data-testid="passenger-search-clear"
                aria-label="Clear search"
              >
                <XCircle className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="passenger-search-result-count">
              {visibleBookings.length} result{visibleBookings.length === 1 ? "" : "s"} for "<span className="font-medium text-amber-700">{searchQuery}</span>"
            </p>
          )}
        </div>
        
        {/* Clickable Filter Badges */}
        <div className="flex flex-wrap gap-1.5 md:gap-2 mt-3 md:mt-4">
          <span className="text-xs md:text-sm text-muted-foreground mr-1 md:mr-2">Filter:</span>
          
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${statusFilter === "pending" ? "bg-yellow-500 text-white" : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"}`} onClick={() => setStatusFilter(statusFilter === "pending" ? null : "pending")}>
            <Clock className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> Pending
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${statusFilter === "confirmed" ? "bg-green-600 text-white" : "bg-green-100 text-green-800 hover:bg-green-200"}`} onClick={() => setStatusFilter(statusFilter === "confirmed" ? null : "confirmed")}>
            <CheckCircle className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> Confirmed
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${statusFilter === "cancelled" ? "bg-red-600 text-white" : "bg-red-100 text-red-800 hover:bg-red-200"}`} onClick={() => setStatusFilter(statusFilter === "cancelled" ? null : "cancelled")}>
            <XCircle className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> Cancelled
          </Badge>
          
          <span className="hidden md:inline text-gray-300 mx-1">|</span>
          
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${paymentFilter === "paid" ? "bg-green-600 text-white" : "bg-green-100 text-green-800 hover:bg-green-200"}`} onClick={() => setPaymentFilter(paymentFilter === "paid" ? null : "paid")}>
            <DollarSign className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> Paid
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${paymentFilter === "pending" ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-800 hover:bg-orange-200"}`} onClick={() => setPaymentFilter(paymentFilter === "pending" ? null : "pending")}>
            <Clock className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> <span className="hidden sm:inline">Payment </span>Pending
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${paymentFilter === "pending_verification" ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-800 hover:bg-blue-200"}`} onClick={() => setPaymentFilter(paymentFilter === "pending_verification" ? null : "pending_verification")}>
            <Eye className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> Verify
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${paymentFilter === "deposit_unpaid" ? "bg-rose-600 text-white" : "bg-rose-100 text-rose-800 hover:bg-rose-200"}`} onClick={() => setPaymentFilter(paymentFilter === "deposit_unpaid" ? null : "deposit_unpaid")} data-testid="filter-deposit-unpaid">
            <DollarSign className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" /> <span className="hidden sm:inline">Deposit </span>Unpaid
          </Badge>
          
          <span className="hidden md:inline text-gray-300 mx-1">|</span>
          
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${passengerStatusFilter === "on_flight" ? "bg-green-600 text-white" : "bg-green-100 text-green-800 hover:bg-green-200"}`} onClick={() => setPassengerStatusFilter(passengerStatusFilter === "on_flight" ? null : "on_flight")}>
            On Flight
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${passengerStatusFilter === "delayed" ? "bg-yellow-500 text-white" : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"}`} onClick={() => setPassengerStatusFilter(passengerStatusFilter === "delayed" ? null : "delayed")}>
            Delayed
          </Badge>
          <Badge className={`cursor-pointer transition-all text-[10px] md:text-xs ${passengerStatusFilter === "missed_flight" ? "bg-red-600 text-white" : "bg-red-100 text-red-800 hover:bg-red-200"}`} onClick={() => setPassengerStatusFilter(passengerStatusFilter === "missed_flight" ? null : "missed_flight")}>
            Missed
          </Badge>
        </div>
        
        {hasActiveFilters && (
          <div className="mt-2 text-sm text-muted-foreground">
            Showing {visibleBookings.length} of {bookings.length} bookings
            {dateFilter && <span className="ml-2 font-medium text-blue-600">Date: {dateFilter}</span>}
            {groupFilter && <span className="ml-2 font-medium text-yellow-600">Groups Only</span>}
            {driverFilter && <span className="ml-2 font-medium text-blue-600">Driver: {driverFilter}</span>}
            {terminalFilter && <span className="ml-2 font-medium text-gray-600">Terminal: {terminalFilter}</span>}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {visibleBookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? `No passengers match "${searchQuery}"`
                : hasActiveFilters
                ? "No bookings match your filters"
                : "No bookings yet"}
            </p>
            {searchQuery && (
              <Button variant="link" onClick={() => setSearchQuery("")} className="mt-2">Clear search</Button>
            )}
            {hasActiveFilters && !searchQuery && (
              <Button variant="link" onClick={clearFilters} className="mt-2">Clear all filters</Button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3" data-testid="bookings-mobile-cards">
              {visibleBookings.map((booking) => {
                const flightNum = (booking.flight_number || '').toUpperCase();
                const airlineCode = getAirlineCode(flightNum);
                const colors = getAirlineColors(airlineCode);
                const grpColor = getGroupColor(booking);
                return (
                  <div key={booking.id} className={`border rounded-xl p-3 space-y-2 hover:shadow-md transition-shadow ${grpColor ? `${grpColor.row} ${grpColor.border}` : ""}`} data-testid={`mobile-booking-${booking.id}`}>
                    {/* Header: Name + Status */}
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sm">{booking.full_name}</p>
                        <p className="text-[10px] text-muted-foreground">{booking.whatsapp_number}</p>
                        {(booking.debt_surcharge || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-800 border border-red-400" data-testid={`debt-badge-${booking.id}`}>
                            <DollarSign className="w-3 h-3" />
                            Prior-balance settlement +${(booking.debt_surcharge || 0).toFixed(0)}
                          </span>
                        )}
                        {booking.deposit_verified ? (
                          <span className="inline-flex items-center gap-1 mt-1 ml-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-400" data-testid={`deposit-verified-badge-${booking.id}`} title={`Verified via ${booking.deposit_verified_method || 'admin'}`}>
                            <ShieldCheck className="w-3 h-3" />
                            Deposit Verified{booking.deposit_verified_method ? ` · ${booking.deposit_verified_method}` : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 mt-1 ml-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800 border border-amber-400" data-testid={`deposit-unverified-badge-${booking.id}`} title="Awaiting deposit — full trip still owed">
                            <AlertTriangle className="w-3 h-3" />
                            Deposit Unverified · owes ${(booking.balance_due || 0).toFixed(0)}
                          </span>
                        )}
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); copyConfirmation(booking); }}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-bold rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                            title="Copy full booking info"
                            data-testid={`copy-confirmation-mobile-${booking.id}`}
                          >
                            {copiedId === booking.id ? <Check className="w-2.5 h-2.5 text-green-700" /> : <Copy className="w-2.5 h-2.5" />}
                            #{getConfirmationNumber(booking)}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => sendFullInfo(booking, e)}
                            disabled={sendingId === booking.id}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold rounded border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                            title="Send full info via WhatsApp + email"
                            data-testid={`send-info-mobile-${booking.id}`}
                          >
                            {sendingId === booking.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Share2 className="w-2.5 h-2.5" />}
                            Send
                          </button>
                        </div>
                        {(booking.passengers && booking.passengers.length > 1) && (
                          <div className="mt-1.5 p-1.5 rounded-lg bg-gradient-to-r from-purple-100 via-fuchsia-100 to-pink-100 border-2 border-purple-400 shadow-sm" data-testid={`passenger-list-mobile-${booking.id}`}>
                            <span className="text-[9px] font-extrabold text-purple-900 uppercase block mb-0.5">🎉 Group ({booking.passengers.length})</span>
                            <div className="flex flex-wrap gap-1">
                              {booking.passengers.map((p, idx) => (
                                <span key={idx} className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-white text-purple-900 border border-purple-300 shadow-sm">
                                  {idx === 0 ? "★" : ""}{p.name || `P${idx + 1}`}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {getMobileStatusBadge(booking.booking_status, "booking")}
                        {getMobileStatusBadge(booking.payment_status, "payment")}
                      </div>
                    </div>
                    {/* Date + Flight */}
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        <button onClick={() => setDateFilter(dateFilter === booking.travel_date ? null : booking.travel_date)}
                          className={`font-medium ${dateFilter === booking.travel_date ? 'text-blue-600' : ''}`}>
                          {(() => {
                            const d = booking.travel_date ? new Date(booking.travel_date + "T12:00:00") : null;
                            if (!d || isNaN(d)) return booking.travel_date;
                            const wd = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/Chicago" });
                            return `${wd}, ${booking.travel_date}`;
                          })()}
                        </button>
                        <span className={`text-sm font-bold px-2 py-0.5 rounded border ${getPickupTimeStyle(booking.travel_time).badge}`} title={getPickupTimeStyle(booking.travel_time).label} data-testid={`booking-row-pickup-time-${booking.id}`}>
                          {getPickupTimeStyle(booking.travel_time).emoji} {format12Hour(booking.travel_time) || booking.travel_time}
                        </span>
                        {booking.trip_type === "return" && (
                          <Badge variant="secondary" className="text-[9px] px-1 py-0 bg-orange-100 text-orange-700">Return</Badge>
                        )}
                      </div>
                      {flightNum && (
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-white font-bold text-[8px] ${colors.bg}`}>{airlineCode || '??'}</span>
                          <span className={`text-xs font-semibold ${colors.text}`}>{flightNum}</span>
                        </div>
                      )}
                    </div>
                    {booking.created_at && (
                      <p className="text-[9px] text-gray-400">Booked: {fmtChicagoDate(booking.created_at)} {fmtChicagoTime(booking.created_at)} CT</p>
                    )}
                    {/* Route */}
                    <p className="text-[11px] text-muted-foreground truncate">{booking.pickup_location} → {booking.dropoff_location || booking.housing_unit}</p>
                    {/* Money */}
                    <div className="flex items-center justify-between text-xs bg-gray-50 rounded-lg p-2">
                      <div>
                        <span className="font-bold">${(booking.trip_price || 0).toFixed(2)}</span>
                        <span className="text-muted-foreground ml-1">total</span>
                      </div>
                      <div>
                        <span className="text-green-700 font-semibold">${(booking.amount_paid || 0).toFixed(2)}</span>
                        <span className="text-muted-foreground ml-1">paid</span>
                      </div>
                      <div>
                        <span className={`font-bold ${(booking.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>${(booking.balance_due || 0).toFixed(2)}</span>
                        <span className="text-muted-foreground ml-1">due</span>
                      </div>
                      <div>
                        {booking.deposit_verified ? (
                          <Badge className="text-[8px] bg-green-100 text-green-700 px-1 py-0"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />Verified</Badge>
                        ) : (
                          <Badge className="text-[8px] bg-amber-100 text-amber-700 px-1 py-0"><ShieldAlert className="w-2.5 h-2.5 mr-0.5" />Unverified</Badge>
                        )}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="grid grid-cols-4 gap-1.5 pt-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => setSelectedBooking(booking)} className="w-full h-8 text-[10px] gap-1">
                            <Eye className="w-3 h-3" /> View
                          </Button>
                        </DialogTrigger>
                        <BookingDetailsDialog selectedBooking={selectedBooking} drivers={drivers} getAuthHeaders={getAuthHeaders} fetchData={fetchData} onClose={() => setSelectedBooking(null)}
                          onCreateReturnTrip={(b) => {
                            setSelectedBooking(null);
                            setCustomBooking({
                              trip_type: "return",
                              ride_type: b.ride_type || "shared",
                              full_name: b.full_name || "",
                              whatsapp_number: b.whatsapp_number || "",
                              email: b.email || "",
                              travel_date: "",
                              travel_time: "",
                              departure_time: "",
                              flight_number: "",
                              airline: "",
                              pickup_location: b.dropoff_location || b.housing_unit || "",
                              dropoff_location: b.pickup_location || "",
                              housing_unit: b.dropoff_location || b.housing_unit || "",
                              num_passengers: b.num_passengers || 1,
                              total_suitcases: b.total_suitcases || 0,
                              total_carry_ons: b.total_carry_ons || 0,
                              total_barrels: 0,
                              booking_status: "confirmed",
                              payment_method: b.payment_method || "manual",
                              payment_status: "pending",
                              admin_notes: `Return trip for arrival booking #${(b.id || "").slice(0, 5).toUpperCase()}`,
                              passengers: (b.passengers || []).map(p => ({ name: p.name || "", whatsapp: p.whatsapp || "", housing_unit: p.housing_unit || "" })),
                            });
                            setShowCreateBooking(true);
                          }}
                        />
                      </Dialog>
                      {booking.booking_status === "pending" ? (
                        <ConfirmAction title="Confirm?" description={`Confirm ${booking.full_name}?`} onConfirm={() => confirmBooking(booking.id)} confirmText="Confirm">
                          <Button variant="default" size="sm" className="w-full h-8 text-[10px] gap-1 bg-green-600 hover:bg-green-700"><CheckCircle className="w-3 h-3" /> OK</Button>
                        </ConfirmAction>
                      ) : booking.booking_status === "cancelled" ? (
                        <ConfirmAction title="Restore?" description={`Restore ${booking.full_name}?`} onConfirm={() => uncancelBooking(booking.id)} confirmText="Restore">
                          <Button variant="default" size="sm" className="w-full h-8 text-[10px] gap-1 bg-amber-500"><RotateCcw className="w-3 h-3" /> Undo</Button>
                        </ConfirmAction>
                      ) : (
                        <ConfirmAction title="Cancel?" description={`Cancel ${booking.full_name}?`} onConfirm={() => cancelBooking(booking.id)} confirmText="Cancel" variant="destructive">
                          <Button variant="outline" size="sm" className="w-full h-8 text-[10px] gap-1 text-red-600 border-red-200"><XCircle className="w-3 h-3" /> Cancel</Button>
                        </ConfirmAction>
                      )}
                      <ConfirmAction title="Cash Payment?" description={`Mark ${booking.full_name} paid by cash?`} onConfirm={() => cashPayment(booking.id)} confirmText="Paid">
                        <Button variant="outline" size="sm" className="w-full h-8 text-[10px] gap-1 text-green-700 border-green-300 bg-green-50"><Wallet className="w-3 h-3" /> Cash</Button>
                      </ConfirmAction>
                      <ConfirmAction title="Re-send?" description={`Send confirmation to ${booking.full_name}?`} onConfirm={() => sendConfirmation(booking.id)} confirmText="Send">
                        <Button variant="outline" size="sm" className="w-full h-8 text-[10px] gap-1 text-blue-700 border-blue-300 bg-blue-50"><Send className="w-3 h-3" /> Send</Button>
                      </ConfirmAction>
                    </div>
                    <div className="pt-2" onClick={(e) => e.stopPropagation()}>
                      <BookingNoteEditor booking={booking} isItAdmin={isItAdmin} compact onUpdated={fetchData} />
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full" data-testid="bookings-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Passenger</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Flight</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Route</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Paid</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Balance</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleBookings.map((booking) => {
                  const grpColor = getGroupColor(booking);
                  return (
                  <tr key={booking.id} className={`border-b hover:bg-muted/30 ${grpColor ? `${grpColor.row} ${grpColor.border}` : ""}`}>
                    <td className="p-3">
                      {mergeMode && (
                        <label className="flex items-center gap-1.5 mb-1 cursor-pointer">
                          <input type="checkbox" checked={!!mergeSelected[booking.id]} onChange={(e) => setMergeSelected(prev => ({...prev, [booking.id]: e.target.checked}))} className="w-4 h-4 accent-purple-600" data-testid={`merge-checkbox-${booking.id}`} />
                          <span className="text-[9px] font-bold text-purple-600 uppercase">Merge</span>
                        </label>
                      )}
                      {grpColor && booking.trip_type !== "return" && (
                        <span className={`inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 rounded mb-1 ${grpColor.chip}`} title="Same color = same arrival group">●&nbsp;Group</span>
                      )}
                      <button 
                        onClick={() => setDateFilter(dateFilter === booking.travel_date ? null : booking.travel_date)}
                        className={`font-medium hover:underline cursor-pointer ${dateFilter === booking.travel_date ? 'text-blue-600' : ''}`}
                        title={booking.travel_date}
                      >
                        {(() => {
                          // Parse ISO date "2026-06-19" as a Chicago-local date so weekday is correct
                          const d = booking.travel_date ? new Date(booking.travel_date + "T12:00:00") : null;
                          if (!d || isNaN(d)) return booking.travel_date;
                          const wd = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/Chicago" });
                          return `${wd}, ${booking.travel_date}`;
                        })()}
                      </button>
                      <p className="text-base font-bold text-amber-600 tracking-wide" data-testid={`travel-time-${booking.id}`}>
                        {format12Hour(booking.travel_time) || booking.travel_time}
                      </p>
                      {booking.trip_type === "return" && booking.departure_time && (
                        <p className="text-sm text-orange-600 font-bold">Dept: {format12Hour(booking.departure_time) || booking.departure_time}</p>
                      )}
                      {booking.created_at && (
                        <p className="text-[9px] text-gray-400 mt-0.5">Booked: {fmtChicagoDate(booking.created_at)} {fmtChicagoTime(booking.created_at)} CT</p>
                      )}
                      {booking.trip_type === "return" && (
                        <Badge variant="secondary" className="mt-1 text-[10px] bg-orange-100 text-orange-700">Return</Badge>
                      )}
                      {booking.booking_type === 'group' && (
                        <Badge 
                          variant="secondary" 
                          className={`mt-1 text-xs cursor-pointer hover:opacity-80 ${groupFilter ? 'bg-yellow-300 text-yellow-900' : 'bg-yellow-100 text-yellow-800'}`}
                          onClick={(e) => { e.stopPropagation(); setGroupFilter(groupFilter ? null : true); }}
                        >
                          Group ({booking.num_passengers})
                        </Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{booking.full_name}</p>
                      <p className="text-xs text-muted-foreground">{booking.whatsapp_number}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); copyConfirmation(booking); }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono font-bold rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                          title="Click to copy full booking info"
                          data-testid={`copy-confirmation-${booking.id}`}
                        >
                          {copiedId === booking.id ? (
                            <Check className="w-3 h-3 text-green-700" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                          #{getConfirmationNumber(booking)}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => sendFullInfo(booking, e)}
                          disabled={sendingId === booking.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors disabled:opacity-50"
                          title="Send full booking info via WhatsApp + email"
                          data-testid={`send-info-${booking.id}`}
                        >
                          {sendingId === booking.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Share2 className="w-3 h-3" />
                          )}
                          Send Info
                        </button>
                      </div>
                      {(booking.passengers && booking.passengers.length > 1) && (
                        <div className="mt-2 p-2 rounded-lg bg-gradient-to-r from-purple-100 via-fuchsia-100 to-pink-100 border-2 border-purple-400 shadow-sm" data-testid={`passenger-list-${booking.id}`}>
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-[11px] font-extrabold text-purple-900 uppercase tracking-wide">🎉 Group ({booking.passengers.length})</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {booking.passengers.map((p, idx) => (
                              <span key={idx} className="text-sm font-bold px-2 py-0.5 rounded-full bg-white text-purple-900 border-2 border-purple-300 shadow-sm">
                                {idx === 0 ? "★ " : ""}{p.name || `Passenger ${idx + 1}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {booking.assigned_driver_name && (
                        <Badge 
                          variant="outline" 
                          className={`mt-1 text-xs cursor-pointer hover:opacity-80 ${driverFilter === booking.assigned_driver_name ? 'bg-blue-200 text-blue-900 border-blue-400' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
                          onClick={(e) => { e.stopPropagation(); setDriverFilter(driverFilter === booking.assigned_driver_name ? null : booking.assigned_driver_name); }}
                        >
                          <UserCheck className="w-3 h-3 mr-1" />
                          {booking.assigned_driver_name}
                        </Badge>
                      )}
                      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <BookingNoteEditor booking={booking} isItAdmin={isItAdmin} onUpdated={fetchData} />
                      </div>
                    </td>
                    <td className="p-3">
                      {(() => {
                        const flightNum = (booking.flight_number || '').toUpperCase();
                        const airlineCode = getAirlineCode(flightNum);
                        const colors = getAirlineColors(airlineCode);
                        const status = booking.flight_status;
                        return (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs ${colors.bg}`}>
                              {airlineCode || '??'}
                            </span>
                            <div>
                              <span className={`text-sm font-semibold ${colors.text}`}>{flightNum || 'N/A'}</span>
                              <p className="text-xs text-muted-foreground">{booking.airline || 'Unknown'}</p>
                              {status && (
                                <Badge className={`mt-0.5 text-[10px] px-1.5 py-0 ${
                                  status === 'landed' ? 'bg-green-100 text-green-700' :
                                  status === 'active' ? 'bg-blue-100 text-blue-700' :
                                  status === 'incident' || status === 'diverted' ? 'bg-orange-100 text-orange-700' :
                                  status === 'cancelled' ? 'bg-red-200 text-red-800' :
                                  (status === 'delayed' || (booking.delay_minutes || 0) > 15) ? 'bg-red-100 text-red-700' :
                                  status === 'scheduled' ? 'bg-gray-100 text-gray-600' :
                                  'bg-gray-100 text-gray-600'
                                }`} data-testid={`flight-status-${booking.id}`}>
                                  <Plane className="w-2.5 h-2.5 mr-0.5" />
                                  {status === 'landed' ? 'Landed' :
                                   status === 'active' ? 'In Air' :
                                   status === 'delayed' ? 'Delayed' :
                                   status === 'scheduled' ? 'On Time' :
                                   status === 'cancelled' ? 'Cancelled' :
                                   status === 'incident' ? 'Incident' :
                                   status === 'diverted' ? 'Diverted' :
                                   status.charAt(0).toUpperCase() + status.slice(1)}
                                  {booking.landing_time ? ` ${format12Hour(booking.landing_time) || booking.landing_time}` : ''}
                                </Badge>
                              )}
                              {(booking.delay_minutes || 0) > 0 && (
                                <p className="text-[10px] text-red-600 font-medium">+{booking.delay_minutes} min delay</p>
                              )}
                              {(booking.arrival_terminal || booking.arrival_gate || booking.arrival_baggage) && (
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {[
                                    booking.arrival_terminal ? `T${booking.arrival_terminal}` : '',
                                    booking.arrival_gate ? `Gate ${booking.arrival_gate}` : '',
                                    booking.arrival_baggage ? `Bag ${booking.arrival_baggage}` : ''
                                  ].filter(Boolean).join(' | ')}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {booking.arrival_terminal && (
                        <Badge 
                          variant="outline" 
                          className={`mt-1 text-xs cursor-pointer hover:opacity-80 ${terminalFilter === booking.arrival_terminal ? 'bg-gray-300 text-gray-900' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setTerminalFilter(terminalFilter === booking.arrival_terminal ? null : booking.arrival_terminal); }}
                        >
                          Terminal {booking.arrival_terminal}
                        </Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <p className="text-sm font-medium">{booking.pickup_location}</p>
                      {booking.housing_address && booking.trip_type === "return" && (
                        <p className="text-[11px] text-blue-600">{booking.housing_address}</p>
                      )}
                      <p className="text-xs text-muted-foreground">{booking.dropoff_location}</p>
                      {booking.housing_address && booking.trip_type !== "return" && (
                        <p className="text-[11px] text-blue-600">{booking.housing_address}</p>
                      )}
                      {booking.total_suitcases > 0 && (
                        <p className="text-xs text-amber-600 font-medium mt-0.5">{booking.total_suitcases} suitcase{booking.total_suitcases > 1 ? 's' : ''}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <p className="font-bold">${(booking.trip_price || 0).toFixed(2)}</p>
                      {booking.num_passengers > 1 && (
                        <p className="text-[10px] text-indigo-600">${booking.per_person_price || (booking.trip_price / booking.num_passengers).toFixed(2)}/person</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Deposit: ${(booking.deposit_amount || 0).toFixed(2)}
                        {booking.deposit_method && (
                          <span className={`ml-1 px-1 py-0.5 rounded text-[9px] font-medium ${
                            booking.deposit_method === 'stripe' ? 'bg-blue-100 text-blue-700' :
                            booking.deposit_method === 'cash' ? 'bg-green-100 text-green-700' :
                            booking.deposit_method === 'zelle' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {booking.deposit_method}
                          </span>
                        )}
                      </p>
                      {sharedRideGroups[booking.id] && (
                        <div className="mt-1 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1" data-testid={`shared-ride-${booking.id}`}>
                          <div className="flex items-center gap-1 text-[10px] text-blue-700 font-semibold">
                            <Users className="w-3 h-3" />
                            Shared Ride ({sharedRideGroups[booking.id].totalPax} pax)
                          </div>
                          <p className="text-[10px] text-blue-600">
                            Group Total: ${sharedRideGroups[booking.id].totalPrice.toFixed(2)}
                          </p>
                          <p className="text-[10px] text-blue-600">
                            Group Paid: ${sharedRideGroups[booking.id].totalPaid.toFixed(2)}
                          </p>
                          <p className="text-[10px] font-bold text-red-600">
                            Group Due: ${sharedRideGroups[booking.id].totalBalance.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <p className="font-semibold text-green-700">${(booking.amount_paid || 0).toFixed(2)}</p>
                      {booking.num_passengers > 1 && (
                        <p className="text-[10px] text-green-600">${((booking.amount_paid || 0) / booking.num_passengers).toFixed(2)}/person</p>
                      )}
                      {booking.payment_method && (
                        <Badge variant="outline" className={`text-[10px] mt-0.5 ${
                          booking.payment_method === 'cash' ? 'bg-green-50 text-green-700 border-green-200' :
                          booking.payment_method === 'stripe' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          booking.payment_method === 'zelle' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {booking.payment_method === 'cash' ? 'Cash' :
                           booking.payment_method === 'stripe' ? 'Stripe' :
                           booking.payment_method === 'zelle' ? 'Zelle' :
                           booking.payment_method === 'manual' ? 'Manual' :
                           booking.payment_method}
                        </Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <p className={`font-bold ${(booking.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${(booking.balance_due || 0).toFixed(2)}
                      </p>
                      {booking.num_passengers > 1 && (booking.balance_due || 0) > 0 && (
                        <p className="text-[10px] text-red-500">${booking.per_person_balance || ((booking.balance_due || 0) / booking.num_passengers).toFixed(2)}/person</p>
                      )}
                      {(booking.balance_due || 0) > 0 && (
                        <p className="text-[10px] text-red-500">Due at pickup</p>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(booking.booking_status, "booking")}
                        {getStatusBadge(booking.payment_status, "payment")}
                        {booking.passenger_status && (
                          <Badge className={
                            booking.passenger_status === "on_flight" ? "bg-green-100 text-green-800 border-green-300" :
                            booking.passenger_status === "missed_flight" ? "bg-red-100 text-red-800 border-red-300" :
                            booking.passenger_status === "delayed" ? "bg-yellow-100 text-yellow-800 border-yellow-300" :
                            "bg-gray-100"
                          }>
                            {booking.passenger_status === "on_flight" && "On Flight"}
                            {booking.passenger_status === "missed_flight" && "Missed"}
                            {booking.passenger_status === "delayed" && `Delayed ${booking.delay_minutes ? booking.delay_minutes + 'min' : ''}`}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="grid grid-cols-3 gap-1.5 min-w-[320px]">
                        {/* View Details */}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" onClick={() => setSelectedBooking(booking)} title="View Details" className="w-full gap-1.5 text-gray-700 border-gray-200 hover:bg-gray-50">
                              <Eye className="w-3.5 h-3.5" /><span className="text-xs">View</span>
                            </Button>
                          </DialogTrigger>
                          <BookingDetailsDialog 
                            selectedBooking={selectedBooking}
                            drivers={drivers}
                            getAuthHeaders={getAuthHeaders}
                            fetchData={fetchData}
                            onClose={() => setSelectedBooking(null)}
                            onCreateReturnTrip={(b) => {
                              setSelectedBooking(null);
                              setCustomBooking({
                                trip_type: "return",
                                ride_type: b.ride_type || "shared",
                                full_name: b.full_name || "",
                                whatsapp_number: b.whatsapp_number || "",
                                email: b.email || "",
                                travel_date: "",
                                travel_time: "",
                                departure_time: "",
                                flight_number: "",
                                airline: "",
                                pickup_location: b.dropoff_location || b.housing_unit || "",
                                dropoff_location: b.pickup_location || "",
                                housing_unit: b.dropoff_location || b.housing_unit || "",
                                num_passengers: b.num_passengers || 1,
                                total_suitcases: b.total_suitcases || 0,
                                total_carry_ons: b.total_carry_ons || 0,
                                total_barrels: 0,
                                booking_status: "confirmed",
                                payment_method: b.payment_method || "manual",
                                payment_status: "pending",
                                admin_notes: `Return trip for arrival booking #${(b.id || "").slice(0, 5).toUpperCase()}`,
                                passengers: (b.passengers || []).map(p => ({ name: p.name || "", whatsapp: p.whatsapp || "", housing_unit: p.housing_unit || "" })),
                              });
                              setShowCreateBooking(true);
                            }}
                          />
                        </Dialog>

                        {/* Combine with ORD Route — only for Madison / non-O'Hare arrivals */}
                        {(booking.trip_type || "arrival") === "arrival" && isMadisonOrSmallAirport(booking) && !booking.price_locked && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => combineWithOrdRoute(booking, e)}
                            className="w-full gap-1 border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800"
                            data-testid={`combine-ord-${booking.id}`}
                            title="Combine this Madison/MKE/MDW pickup with the O'Hare route — applies $120 shared price and locks it"
                          >
                            <Link2 className="w-3.5 h-3.5" /><span className="text-xs">Combine w/ ORD</span>
                          </Button>
                        )}

                        {/* Confirm / Cancel / Uncancel */}
                        {booking.booking_status === "pending" ? (
                          <div className="w-full" title={`Paid: $${(booking.amount_paid || 0).toFixed(2)} | Balance: $${(booking.balance_due || 0).toFixed(2)}`}>
                            <ConfirmAction
                              title="Confirm Booking?"
                              description={`Confirm booking for ${booking.full_name}?\nPaid: $${(booking.amount_paid || 0).toFixed(2)} | Balance: $${(booking.balance_due || 0).toFixed(2)}`}
                              onConfirm={() => confirmBooking(booking.id)}
                              confirmText="Yes, Confirm"
                            >
                              <Button variant="default" size="sm" data-testid={`confirm-btn-${booking.id}`} className="w-full gap-1.5 bg-green-600 hover:bg-green-700" title="Confirm Booking">
                                <CheckCircle className="w-3.5 h-3.5" /><span className="text-xs">Confirm</span>
                              </Button>
                            </ConfirmAction>
                            <p className="text-[9px] text-center text-muted-foreground mt-0.5">
                              Paid ${(booking.amount_paid || 0).toFixed(2)}{booking.num_passengers > 1 ? ` (${booking.num_passengers} pax)` : ''}
                            </p>
                          </div>
                        ) : booking.booking_status === "cancelled" ? (
                          <ConfirmAction
                            title="Restore Booking?"
                            description={`Restore the cancelled booking for ${booking.full_name}?`}
                            onConfirm={() => uncancelBooking(booking.id)}
                            confirmText="Yes, Restore"
                          >
                            <Button variant="default" size="sm" data-testid={`uncancel-btn-${booking.id}`} className="w-full gap-1.5 bg-amber-500 hover:bg-amber-600" title="Restore Booking">
                              <RotateCcw className="w-3.5 h-3.5" /><span className="text-xs">Uncancel</span>
                            </Button>
                          </ConfirmAction>
                        ) : (
                          <ConfirmAction
                            title="Cancel Booking?"
                            description={`Cancel booking for ${booking.full_name}? The customer will need to rebook.`}
                            onConfirm={() => cancelBooking(booking.id)}
                            confirmText="Yes, Cancel"
                            variant="destructive"
                          >
                            <Button variant="outline" size="sm" className="w-full gap-1.5 text-red-600 border-red-200 hover:bg-red-50" title="Cancel Booking">
                              <XCircle className="w-3.5 h-3.5" /><span className="text-xs">Cancel</span>
                            </Button>
                          </ConfirmAction>
                        )}

                        {/* Cash Payment */}
                        <ConfirmAction
                          title="Record Cash Payment?"
                          description={`Mark booking for ${booking.full_name} as fully paid by cash?\n\nTrip Total: $${(booking.trip_price || 0).toFixed(2)}\nAlready Paid: $${(booking.amount_paid || 0).toFixed(2)}\n\nUse "Undo Payment" if pressed by mistake.`}
                          onConfirm={() => cashPayment(booking.id)}
                          confirmText="Yes, Mark Paid"
                        >
                          <Button variant="outline" size="sm" className="w-full gap-1.5 text-green-700 border-green-300 bg-green-50 hover:bg-green-100" title="Record Cash Payment" data-testid={`cash-payment-btn-${booking.id}`}>
                            <Wallet className="w-3.5 h-3.5" /><span className="text-xs">Cash</span>
                          </Button>
                        </ConfirmAction>

                        {/* Take Payment */}
                        <Dialog open={paymentDialog === booking.id} onOpenChange={(open) => { setPaymentDialog(open ? booking.id : null); if (!open) setPaymentAmount(""); }}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full gap-1.5 text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100" title="Take Payment via Stripe">
                              <CreditCard className="w-3.5 h-3.5" /><span className="text-xs">Take Payment</span>
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-sm">
                            <DialogHeader>
                              <DialogTitle>Take Payment - {booking.full_name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm" data-testid="payment-summary-dialog">
                                <p className="font-semibold text-gray-700 mb-2">Payment Summary</p>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Trip Price ({booking.num_passengers} pax)</span>
                                  <span className="font-medium">${(booking.trip_price || 0).toFixed(2)}</span>
                                </div>
                                {booking.num_passengers > 1 && (
                                  <div className="flex justify-between text-xs text-muted-foreground">
                                    <span className="ml-3">${(booking.per_person_price || (booking.trip_price / booking.num_passengers)).toFixed(2)}/person</span>
                                    <span></span>
                                  </div>
                                )}
                                {(booking.luggage_fee || 0) > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Luggage Fee</span>
                                    <span className="font-medium">${(booking.luggage_fee || 0).toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-green-700">
                                  <span>Already Paid</span>
                                  <span className="font-semibold">${(booking.amount_paid || 0).toFixed(2)}</span>
                                </div>
                                {booking.num_passengers > 1 && (
                                  <div className="flex justify-between text-xs text-green-600">
                                    <span className="ml-3">${((booking.amount_paid || 0) / booking.num_passengers).toFixed(2)}/person</span>
                                    <span></span>
                                  </div>
                                )}
                                <div className="flex justify-between border-t pt-2 mt-2">
                                  <span className="font-semibold text-red-700">Balance Remaining</span>
                                  <span className="font-bold text-red-700">${(booking.balance_due || 0).toFixed(2)}</span>
                                </div>
                                {booking.num_passengers > 1 && (booking.balance_due || 0) > 0 && (
                                  <div className="flex justify-between text-xs text-red-500">
                                    <span className="ml-3">${(booking.per_person_balance || ((booking.balance_due || 0) / booking.num_passengers)).toFixed(2)}/person</span>
                                    <span></span>
                                  </div>
                                )}
                              </div>
                              <div>
                                <Label>Amount to Charge ($)</Label>
                                <Input type="number" step="0.01" min="0" placeholder={`Suggested: $${(booking.balance_due || 0).toFixed(2)}`} value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} data-testid="payment-amount-input" />
                              </div>
                              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => handleTakePayment(booking.id)} disabled={actionLoading} data-testid="generate-payment-link-btn">
                                <Link2 className="w-4 h-4 mr-2" />
                                {actionLoading ? "Generating..." : "Generate Payment Link"}
                              </Button>
                              <p className="text-xs text-muted-foreground text-center">Opens Stripe checkout & copies link to clipboard</p>
                            </div>
                          </DialogContent>
                        </Dialog>

                        {/* Request Zelle */}
                        <Dialog open={zelleDialog === booking.id} onOpenChange={(open) => { setZelleDialog(open ? booking.id : null); if (!open) setZelleContact(""); }}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full gap-1.5 text-purple-700 border-purple-300 bg-purple-50 hover:bg-purple-100" title="Request Zelle Payment">
                              <Banknote className="w-3.5 h-3.5" /><span className="text-xs">Request Zelle</span>
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-sm">
                            <DialogHeader>
                              <DialogTitle>Request Zelle - {booking.full_name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Trip Price</span>
                                  <span className="font-medium">${(booking.trip_price || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-green-700">
                                  <span>Already Paid</span>
                                  <span className="font-semibold">${(booking.amount_paid || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-t pt-2 text-red-700">
                                  <span className="font-semibold">Balance Due</span>
                                  <span className="font-bold">${(booking.balance_due || 0).toFixed(2)}</span>
                                </div>
                                {booking.num_passengers > 1 && (booking.balance_due || 0) > 0 && (
                                  <p className="text-xs text-red-500 text-right">${(booking.per_person_balance || ((booking.balance_due || 0) / booking.num_passengers)).toFixed(2)}/person</p>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">Send Zelle payment request via WhatsApp to {booking.whatsapp_number}</p>
                              <div>
                                <Label>Your Zelle Phone or Email</Label>
                                <Input type="text" placeholder="e.g. 815-914-8139 or email@gmail.com" value={zelleContact} onChange={(e) => setZelleContact(e.target.value)} data-testid="zelle-contact-input" />
                              </div>
                              <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => handleRequestZelle(booking.id)} disabled={actionLoading} data-testid="send-zelle-request-btn">
                                <Send className="w-4 h-4 mr-2" />
                                {actionLoading ? "Sending..." : "Send Zelle Request via WhatsApp"}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>

                        {/* Verify Payment */}
                        {booking.payment_status === "pending_verification" && (
                          <ConfirmAction
                            title="Verify Payment?"
                            description={`Confirm that payment has been verified for ${booking.full_name}?\nAmount Paid: $${(booking.amount_paid || 0).toFixed(2)}`}
                            onConfirm={() => verifyPayment(booking.id)}
                            confirmText="Yes, Verify"
                          >
                            <Button variant="outline" size="sm" data-testid={`verify-payment-btn-${booking.id}`} className="w-full col-span-3 gap-1.5 text-green-700 border-green-300 bg-green-50 hover:bg-green-100" title="Verify Payment">
                              <CheckCircle className="w-3.5 h-3.5" /><span className="text-xs">Verify Payment</span>
                            </Button>
                          </ConfirmAction>
                        )}

                        {/* Undo Payment */}
                        {booking.payment_status === "paid" && (
                          <ConfirmAction
                            title="Undo Payment?"
                            description={`Reset payment status to pending for ${booking.full_name}?\nThis will NOT refund any Stripe deposits.\nAmount currently marked paid: $${(booking.amount_paid || 0).toFixed(2)}`}
                            onConfirm={() => undoPayment(booking.id)}
                            confirmText="Yes, Undo"
                            variant="destructive"
                          >
                            <Button variant="outline" size="sm" data-testid={`undo-payment-btn-${booking.id}`} className="w-full col-span-3 gap-1.5 text-orange-700 border-orange-300 bg-orange-50 hover:bg-orange-100" title="Undo Payment">
                              <RotateCcw className="w-3.5 h-3.5" /><span className="text-xs">Undo Payment</span>
                            </Button>
                          </ConfirmAction>
                        )}

                        {/* Re-send Confirmation */}
                        <ConfirmAction
                          title="Re-send Confirmation?"
                          description={`Send booking confirmation to ${booking.full_name} (${booking.whatsapp_number})?`}
                          onConfirm={() => sendConfirmation(booking.id)}
                          confirmText="Yes, Send"
                        >
                          <Button variant="outline" size="sm" data-testid={`send-confirm-btn-${booking.id}`} className="w-full col-span-3 gap-1.5 text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100 font-semibold" title="Re-send Confirmation">
                            <Send className="w-3.5 h-3.5" />
                            <span className="text-xs">Re-send Confirmation {formatConfirmation(booking.id)}</span>
                          </Button>
                        </ConfirmAction>

                        {/* Send Custom Message */}
                        <Button variant="outline" size="sm" onClick={() => setMessageDialog(booking)} className="w-full col-span-3 gap-1.5 text-green-700 border-green-300 bg-green-50 hover:bg-green-100" data-testid={`message-btn-${booking.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /><span className="text-xs">Message {booking.full_name?.split(' ')[0]}</span>
                        </Button>

                        {/* Deposit Verification */}
                        {booking.deposit_verified ? (
                          <ConfirmAction
                            title="Remove Deposit Verification?"
                            description={`Unverify deposit for ${booking.full_name}? Verified via ${booking.deposit_verified_method || 'unknown'}.`}
                            onConfirm={() => unverifyDeposit(booking.id)}
                            confirmText="Unverify"
                            variant="destructive"
                          >
                            <Button variant="outline" size="sm" className="w-full col-span-3 gap-1.5 text-green-700 border-green-300 bg-green-50 hover:bg-green-100" data-testid={`deposit-verified-${booking.id}`}>
                              <ShieldCheck className="w-3.5 h-3.5" /><span className="text-xs">Deposit Verified ({booking.deposit_verified_method || '?'})</span>
                            </Button>
                          </ConfirmAction>
                        ) : (
                          <div className="w-full col-span-3 grid grid-cols-3 gap-1">
                            <ConfirmAction title="Verify via Stripe?" description={`Mark deposit verified via Stripe for ${booking.full_name}?`} onConfirm={() => verifyDeposit(booking.id, 'stripe')} confirmText="Verify">
                              <Button variant="outline" size="sm" className="w-full gap-1 text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100 text-[10px]" data-testid={`verify-stripe-${booking.id}`}>
                                <ShieldAlert className="w-3 h-3" /> Stripe
                              </Button>
                            </ConfirmAction>
                            <ConfirmAction title="Verify via Zelle?" description={`Mark deposit verified via Zelle for ${booking.full_name}?`} onConfirm={() => verifyDeposit(booking.id, 'zelle')} confirmText="Verify">
                              <Button variant="outline" size="sm" className="w-full gap-1 text-purple-600 border-purple-200 bg-purple-50 hover:bg-purple-100 text-[10px]" data-testid={`verify-zelle-${booking.id}`}>
                                <ShieldAlert className="w-3 h-3" /> Zelle
                              </Button>
                            </ConfirmAction>
                            <ConfirmAction title="Verify via Bank/Cash?" description={`Mark deposit verified via Bank Transfer/Cash for ${booking.full_name}?`} onConfirm={() => verifyDeposit(booking.id, 'bank_transfer')} confirmText="Verify">
                              <Button variant="outline" size="sm" className="w-full gap-1 text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-[10px]" data-testid={`verify-bank-${booking.id}`}>
                                <ShieldAlert className="w-3 h-3" /> Bank
                              </Button>
                            </ConfirmAction>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </CardContent>
    </Card>

    {/* Send Message Dialog */}
    <Dialog open={!!messageDialog} onOpenChange={(open) => { if (!open) { setMessageDialog(null); setMessageText(""); } }}>
      <DialogContent className="sm:max-w-lg border-2 border-green-200 shadow-2xl">
        <DialogHeader className="bg-green-50 -mx-6 -mt-6 px-6 py-4 rounded-t-lg border-b border-green-200">
          <DialogTitle className="flex items-center gap-3 text-lg">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold">Send WhatsApp Message</p>
              <p className="text-sm font-normal text-muted-foreground">{messageDialog?.full_name} - {messageDialog?.whatsapp_number}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        {messageDialog && (
          <div className="space-y-4 pt-2">
            <Textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              className="border-2 border-gray-200 focus:border-green-400 text-base"
              data-testid="message-textarea"
            />
            <div className="flex gap-3">
              <Button
                className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-base font-semibold"
                disabled={!messageText.trim()}
                onClick={async () => {
                  await sendMessageToPassenger(messageDialog.id, messageText);
                  setMessageDialog(null);
                  setMessageText("");
                }}
                data-testid="send-message-btn"
              >
                <Send className="w-5 h-5 mr-2" /> Send via WhatsApp
              </Button>
              <Button variant="outline" className="h-12" onClick={() => { setMessageDialog(null); setMessageText(""); }}>Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
